// pages/EstimationSemblablesPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import i18next from "i18next";
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation, Trans } from "react-i18next";
import { supabase } from '../lib/supabaseClient';
import AutoCUPAssistant from '../components/AutoCUPAssistant';
import ArtistMarketRatioTool from '../components/ArtistMarketRatioTool';
const getLocaleCode = () => {
    const language = i18next.resolvedLanguage || i18next.language || "fr";
    if (language.startsWith("de")) return "de-DE";
    if (language.startsWith("en")) return "en-GB";
    return "fr-FR";
};
// =====================================================
// Comparables expert
// =====================================================
// Nombre de comparables retenus automatiquement par AutoCUP
// Nombre max que l'expert peut conserver manuellement
const MAX_MANUAL_COMPARABLES = 20;
function isAbortLikeError(error) {
    const message = String(error?.message || error?.details || error?.hint || error || '').toLowerCase();
    return (error?.name === 'AbortError' ||
        message.includes('aborterror') ||
        message.includes('signal is aborted') ||
        message.includes('request was aborted') ||
        message.includes('aborted without reason'));
}
async function runWithAbortRetry(operation, { retries = 1, delayMs = 250, label = 'requête' } = {}) {
    let lastError = null;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
        try {
            return await operation();
        }
        catch (error) {
            lastError = error;
            if (!isAbortLikeError(error) || attempt >= retries)
                break;
            console.warn(`${label} annulée par le navigateur, nouvelle tentative ${attempt + 1}/${retries}.`, error?.message || error);
            await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
        }
    }
    throw lastError;
}
function normalizeKey(value) {
    // Underscores — aligné sur ArtistQuotes.jsx et la migration SQL des artist_key en base
    return String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}
// Alias identique — conservé pour les appels explicites sur medium_key
function normalizeKeyUnderscore(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}
const GROUP_C_METHOD_GROUPS = ['point_method', 'hybrid_points_cup'];
function isGroupCMethodGroup(methodGroup) {
    return GROUP_C_METHOD_GROUPS.includes(String(methodGroup || ''));
}
function artistKeyVariants(value) {
    const underscored = normalizeKey(value);
    const spaced = underscored.replace(/_/g, ' ');
    return Array.from(new Set([underscored, spaced].filter(Boolean)));
}
const SEGMENT_LABELS = {
    primary: () => i18next.t("estimationSemblables.segment.primary"),
    secondary: () => i18next.t("estimationSemblables.segment.secondary"),
    analog_trend: () => i18next.t("estimationSemblables.segment.analogTrend"),
    analog_context: () => i18next.t("estimationSemblables.segment.analogContext"),
};
function getComparableSegment(row, target) {
    const sameArtist = normalizeKey(row?.artist) === normalizeKey(target?.artist);
    const sameTechnique = row?.medium_normalized_key &&
        target?.medium_normalized_key &&
        row.medium_normalized_key === target.medium_normalized_key;
    const sameFamily = row?.medium_family_key &&
        target?.medium_family_key &&
        row.medium_family_key === target.medium_family_key;
    if (sameArtist && sameTechnique)
        return 'primary';
    if (sameArtist && sameFamily)
        return 'secondary';
    if (row?.relationType === 'analog' && sameFamily) {
        return 'analog_trend';
    }
    return 'analog_context';
}
function toNumber(value) {
    if (value === null || value === undefined || value === '')
        return null;
    const n = Number(String(value).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
}
function formatEuro(value) {
    const n = toNumber(value);
    if (n === null)
        return '—';
    return new Intl.NumberFormat(getLocaleCode(), { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(n));
}
function getArtworkHeight(artwork) {
    const dims = artwork?.specifications?.dimensions || {};
    return toNumber(artwork?.height ?? artwork?.dimensions_height ?? dims.height);
}
function getArtworkWidth(artwork) {
    const dims = artwork?.specifications?.dimensions || {};
    return toNumber(artwork?.width ?? artwork?.dimensions_width ?? dims.width);
}
// ── Bonus documentaires (IFRS 13 / méthodologie CUP) ─────────────────────────
// Certificat d'authenticité    +5%  : certificate_auth OU certificate_authenticity renseigné
// Provenance artiste (1er mché)+2%  : provenance array contient sortie atelier/galerie représentante
//                                     OU acquisition_type primary/gallery avec source renseignée
// Tracking impeccable          +5%  : chaîne ininterrompue depuis atelier (provenance ≥2 items + invoice)
function computeDocumentaryBonuses(artwork = {}) {
    const bonuses = [];
    // ── Parser le JSONB documents une seule fois ──────────────────────────────
    // documents est stocké en BDD comme string JSON ou tableau déjà parsé.
    // Chaque entrée a un champ `type` : 'certificate' | 'invoice' | 'condition_report' | 'other'
    let docsArray = [];
    try {
        const raw = artwork.documents;
        if (Array.isArray(raw))
            docsArray = raw;
        else if (typeof raw === 'string' && raw.trim())
            docsArray = JSON.parse(raw);
    }
    catch (_) {
        docsArray = [];
    }
    const docTypes = new Set(docsArray.map((d) => String(d?.type || '').toLowerCase()));
    // ── Donation directe artiste ──────────────────────────────────────────────
    const donationText = String(artwork.acquisition_type ?? artwork.acquisitionType ?? artwork.purchase_type ??
        artwork.acquisition_source ?? artwork.acquisitionSource ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const isDonation = /donation|don\b|offert|gift|given by artist|offert par l.artiste|acte de don/.test(donationText) ||
        /donation|don\b|offert|gift|given by artist|offert par l.artiste|acte de don/.test(String(artwork.provenance ?? artwork.notes ?? artwork.comment ?? '').toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
    if (isDonation) {
        bonuses.push({ label: i18next.t("estimationSemblables.ui.donationDirecteDeLArtisteEquivCertificat"), pct: 5 });
    }
    // ── Certificat d'authenticité ─────────────────────────────────────────────
    // Sources : flag booléen BDD, champ texte, ou document de type 'certificate' dans documents[]
    const hasCertificate = !isDonation && Boolean(artwork.certificate_auth ||
        artwork.certificate ||
        artwork.has_certificate ||
        artwork.authenticity_certificate ||
        (artwork.certificate_authenticity && String(artwork.certificate_authenticity).trim()) ||
        docTypes.has('certificate') // ← document uploadé dans l'onglet Documents
    );
    if (hasCertificate) {
        bonuses.push({ label: i18next.t("estimationSemblables.ui.certificatDAuthenticite"), pct: 5 });
    }
    // ── Provenance artiste / 1er marché ──────────────────────────────────────
    const rawProv = artwork.provenance ??
        artwork.provenance_history ??
        artwork.provenanceHistory ??
        artwork.provenance_notes ??
        artwork.provenanceNotes ??
        artwork.origin ??
        artwork.source_history ??
        null;
    const provArray = Array.isArray(rawProv)
        ? rawProv
        : rawProv && typeof rawProv === 'object'
            ? Object.values(rawProv).map((value) => ({ description: String(value || '') }))
            : rawProv && typeof rawProv === 'string'
                ? [{ description: rawProv }]
                : [];
    const provText = [
        ...provArray.map((item) => typeof item === 'string'
            ? item
            : [item.description, item.source, item.name, item.type, item.title, item.gallery, item.dealer]
                .filter(Boolean).join(' ')),
        artwork.acquisition_source,
        artwork.acquisitionSource,
        artwork.gallery,
        artwork.seller,
        artwork.purchase_source,
        artwork.purchaseSource,
    ].filter(Boolean).join(' ');
    const normalizedProvText = String(provText || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const acquisitionType = String(artwork.acquisition_type ?? artwork.acquisitionType ?? artwork.purchase_type ?? '').toLowerCase();
    const primaryKeywords = /atelier|artiste|artist|studio|sortie d.?atelier|1er march|premier march|first market|primary|galerie repr[ée]sent|galerie representante|representing gallery|gallery direct|galerie directe|acquis.?aupres de l.?artiste|acquis.?aupres de la galerie/i;
    const hasPrimaryProvenance = primaryKeywords.test(normalizedProvText) ||
        ['primary', 'first_market', 'first market', 'gallery', 'galerie', 'artist', 'atelier', 'studio'].includes(acquisitionType) ||
        (['primary', 'gallery', 'galerie'].includes(acquisitionType) &&
            Boolean(artwork.acquisition_source || artwork.acquisitionSource));
    if (hasPrimaryProvenance) {
        bonuses.push({ label: i18next.t("estimationSemblables.ui.provenanceArtiste1erMarche"), pct: 2 });
    }
    // ── Suivi documentaire complet ────────────────────────────────────────────
    // Facture : flag BDD ou document de type 'invoice' dans documents[]
    const hasInvoice = Boolean(artwork.invoice === true ||
        artwork.has_invoice === true ||
        artwork.invoice_url ||
        artwork.invoiceUrl ||
        artwork.purchase_invoice ||
        docTypes.has('invoice') // ← facture uploadée dans l'onglet Documents
    );
    const hasFullTracking = hasPrimaryProvenance && provArray.length >= 2 && hasInvoice;
    if (hasFullTracking) {
        bonuses.push({ label: i18next.t("estimationSemblables.ui.suiviDocumentaireComplet"), pct: 5 });
    }
    const totalPct = bonuses.reduce((sum, b) => sum + b.pct, 0);
    return { bonuses, totalPct };
}
// ── Décote conservation (grille IFRS 13 / méthodologie CUP) ─────────────
function getConditionDiscount(condition) {
    if (condition === null || condition === undefined || condition === '') {
        return { pct: 0, label: null, isUnrecognized: false };
    }
    const c = String(condition).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    // Clés canoniques BDD (valeurs exactes stockées par le formulaire ArtworkDetail)
    if (c === 'irreparable')
        return { pct: 100, label: i18next.t("estimationSemblables.ui.irreparableWriteOff") };
    if (c === 'partially_restorable')
        return { pct: 65, label: i18next.t("estimationSemblables.ui.partiellementRestaurable65") };
    if (c === 'heavily_damaged')
        return { pct: 50, label: i18next.t("estimationSemblables.ui.tresAbimeRestaurable50") };
    if (c === 'very_poor')
        return { pct: 30, label: i18next.t("estimationSemblables.ui.tresMoyen30") };
    if (c === 'fair')
        return { pct: 20, label: i18next.t("estimationSemblables.ui.etatMoyen20") };
    // États sans décote (bon, très bon, excellent) — explicitement neutres
    if (/^(excellent|tres.?bon|very.?good|good|bon|neuf|parfait)$/.test(c)) {
        return { pct: 0, label: null };
    }
    // Texte libre legacy (fallback regex) — ordre du plus grave au moins grave
    if (/irreparable/.test(c))
        return { pct: 100, label: i18next.t("estimationSemblables.ui.irreparableWriteOff") };
    if (/partiellement.?restaurable|partially/.test(c))
        return { pct: 65, label: i18next.t("estimationSemblables.ui.partiellementRestaurable65") };
    if (/tres.?abime|heavily.?damaged|tres.?abime/.test(c))
        return { pct: 50, label: i18next.t("estimationSemblables.ui.tresAbimeRestaurable50") };
    if (/tres.?moyen|very.?poor/.test(c))
        return { pct: 30, label: i18next.t("estimationSemblables.ui.tresMoyen30") };
    if (/moyen|fair/.test(c))
        return { pct: 20, label: i18next.t("estimationSemblables.ui.etatMoyen20") };
    // Valeur présente mais non reconnue par la grille → 0% décote + flag diagnostic
    console.warn(`[getConditionDiscount] Valeur d'état non reconnue : "${condition}" — aucune décote appliquée. Vérifier la valeur en BDD.`);
    return { pct: 0, label: null, isUnrecognized: true, rawValue: condition };
}
function getLiquidityDiscountPct({ artwork, artistQuote, methodGroup }) {
    const explicit = toNumber(artwork?.liquidity_discount_pct ??
        artwork?.liquidityDiscountPct ??
        artistQuote?.liquidity_discount_pct ??
        artistQuote?.liquidityDiscountPct);
    if (explicit !== null)
        return Math.max(0, Math.min(95, Math.abs(explicit)));
    // Méthodologie Groupe C : abattement de liquidité −30 à −55 %
    // en fonction de l'absence de marché secondaire et de l'étroitesse du marché primaire.
    if (isGroupCMethodGroup(methodGroup)) {
        const confidence = String(artistQuote?.confidence || '').toLowerCase();
        if (confidence === 'high' || confidence === 'haute')
            return 30;
        if (confidence === 'low' || confidence === 'faible')
            return 55;
        return 45;
    }
    return 0;
}
const getGroupCLiquidityClientNote = () => i18next.t("estimationSemblables.dynamic.groupCLiquidityNote");
function buildAutoExpertNote({ expertFreeNote, artistQuote, quoteSuggestion, adjustmentNote, galleryCapNum }) {
    const free = String(expertFreeNote || '').trim();
    if (free)
        return free;
    const isGroupC = isGroupCMethodGroup(artistQuote?.method_group) || quoteSuggestion?.method === 'point_method';
    const parts = [];
    if (quoteSuggestion?.isWriteOff) {
        parts.push(i18next.t("estimationSemblables.dynamic.writeOffNote"));
    }
    if (quoteSuggestion?.summary)
        parts.push(quoteSuggestion.summary);
    if (adjustmentNote)
        parts.push(adjustmentNote);
    if (isGroupC) {
        parts.push(getGroupCLiquidityClientNote());
    }
    if (galleryCapNum) {
        parts.push(`Borne haute galerie (indicative) : ${formatEuro(galleryCapNum)}`);
    }
    return parts.filter(Boolean).join(' · ') || null;
}
function getComparableMarketSegment(c = {}) {
    return c.marketSegment || c.market_segment || c.segment || '';
}
function isOwnTechnicalSegment(c = {}, artwork = {}) {
    // Un comparable marqué is_analog_comparable (extrapolation via ratio pivot)
    // ne compte jamais comme résultat propre — même si l'artiste correspond.
    if (c.is_analog_comparable)
        return false;
    if (['analog', 'analogy', 'analog_trend', 'analog_context'].includes(getComparableMarketSegment(c)))
        return false;
    const segment = getComparableMarketSegment(c);
    if (['primary', 'secondary', 'own_context'].includes(segment))
        return true;
    const targetArtist = normalizeKey(artwork?.artist || '');
    const compArtist = normalizeKey(c.artist || '');
    return Boolean(targetArtist && compArtist && targetArtist === compArtist);
}
function getComparableSegmentCounts(validComparables = []) {
    return validComparables.reduce((acc, c) => {
        const segment = getComparableMarketSegment(c) || 'manual';
        acc[segment] = (acc[segment] || 0) + 1;
        return acc;
    }, {});
}
function getPrudenceDiscountPct({ artistQuote, methodGroup, validComparables, artwork, hasOwnRecentFromAutoCUP = false }) {
    if (methodGroup !== 'analogy_cup')
        return 0;
    const explicit = toNumber(artistQuote?.prudence_discount_pct ??
        artistQuote?.prudent_discount_pct ??
        artistQuote?.analog_discount_pct ??
        artistQuote?.default_discount_pct);
    if (explicit !== null)
        return Math.max(0, Math.min(95, Math.abs(explicit)));
    const completeComparables = Array.isArray(validComparables)
        ? validComparables.filter((c) => c?.title && c?.artist && c?.price)
        : [];
    const hasOwnRecent = hasOwnRecentFromAutoCUP || completeComparables.some((c) => isOwnTechnicalSegment(c, artwork) && isRecentComparable(c));
    const hasPrimarySegment = completeComparables.some((c) => getComparableMarketSegment(c) === 'primary');
    // Méthodologie Groupe B : la prudence ne saute que si le socle technique propre existe.
    if (hasPrimarySegment && hasOwnRecent)
        return 0;
    if (hasOwnRecent)
        return 8;
    return 15;
}
function multiplyRange(range, factor) {
    return {
        low: range.low !== null && range.low !== undefined ? Math.round(range.low * factor) : null,
        high: range.high !== null && range.high !== undefined ? Math.round(range.high * factor) : null,
        retained: range.retained !== null && range.retained !== undefined ? Math.round(range.retained * factor) : null,
    };
}
function applyFinalMethodologyAdjustments({ low, high, retained, artwork, artistQuote, methodGroup, validComparables = [], includeCondition = true, includeDocumentaryBonuses = true, includePrudence = true, includeLiquidity = true, hasOwnRecentFromAutoCUP = false, 
// Quand une cotation personnelle porte une valeur moyenne validée par l'expert,
// cette valeur est une décision expert et non une simple médiane mécanique.
// On ajuste la fourchette. Pour les cotations expertes Groupe B par analogie,
// la valeur retenue peut être une décision d'expert déjà bonus compris
// (ex. Verschaere à 2 500 €). Pour les CUP directes, on laisse les bonus
// documentaires agir aussi sur la valeur retenue.
preserveRetained = false, }) {
    const baseLow = toNumber(low);
    const baseHigh = toNumber(high) ?? baseLow;
    const baseRetained = toNumber(retained) ?? (baseLow !== null && baseHigh !== null
        ? Math.round((baseLow + baseHigh) / 2)
        : baseLow ?? baseHigh ?? null);
    let current = {
        low: baseLow,
        high: baseHigh,
        retained: baseRetained,
    };
    const notes = [];
    const details = {
        baseLow,
        baseHigh,
        baseRetained,
        conditionDiscount: null,
        documentaryBonuses: [],
        documentaryBonusPct: 0,
        prudenceDiscountPct: 0,
        liquidityDiscountPct: 0,
        segmentCounts: getComparableSegmentCounts(validComparables),
        factors: [],
    };
    // 1. État d'abord, conformément à la grille.
    if (includeCondition) {
        const conditionDiscount = getConditionDiscount(artwork?.condition);
        const conditionFactor = (100 - conditionDiscount.pct) / 100;
        details.conditionDiscount = conditionDiscount;
        details.factors.push({ label: i18next.t("estimationSemblables.ui.etatDeConservation"), factor: conditionFactor });
        if (conditionDiscount.pct === 100) {
            current = { low: 0, high: 0, retained: 0 };
            notes.push(conditionDiscount.label);
        }
        else if (conditionDiscount.pct > 0) {
            current = multiplyRange(current, conditionFactor);
            notes.push(i18next.t("estimationSemblables.dynamic.conditionDiscount", { label: conditionDiscount.label }));
        }
    }
    // 2. Décote prudentielle Groupe B, si besoin.
    if (includePrudence) {
        const prudencePct = getPrudenceDiscountPct({ artistQuote, methodGroup, validComparables, artwork, hasOwnRecentFromAutoCUP });
        const prudenceFactor = (100 - prudencePct) / 100;
        details.prudenceDiscountPct = prudencePct;
        details.factors.push({ label: i18next.t("estimationSemblables.ui.decotePrudentielle"), factor: prudenceFactor });
        if (prudencePct > 0) {
            current = multiplyRange(current, prudenceFactor);
            notes.push(i18next.t("estimationSemblables.dynamic.prudenceDiscount", { pct: prudencePct }));
        }
    }
    // 3. Abattement de liquidité Groupe C.
    if (includeLiquidity) {
        const liquidityPct = getLiquidityDiscountPct({ artwork, artistQuote, methodGroup });
        const liquidityFactor = (100 - liquidityPct) / 100;
        details.liquidityDiscountPct = liquidityPct;
        details.factors.push({ label: i18next.t("estimationSemblables.ui.abattementLiquidite"), factor: liquidityFactor });
        if (liquidityPct > 0) {
            current = multiplyRange(current, liquidityFactor);
            notes.push(i18next.t("estimationSemblables.dynamic.liquidityDiscount", { pct: liquidityPct }));
        }
    }
    // 4. Bonifications documentaires ensuite, cumulables.
    if (includeDocumentaryBonuses) {
        const { bonuses, totalPct } = computeDocumentaryBonuses(artwork || {});
        const bonusFactor = 1 + totalPct / 100;
        details.documentaryBonuses = bonuses;
        details.documentaryBonusPct = totalPct;
        details.factors.push({ label: i18next.t("estimationSemblables.ui.bonificationsDocumentaires"), factor: bonusFactor });
        if (totalPct > 0) {
            current = multiplyRange(current, bonusFactor);
            notes.push(i18next.t("estimationSemblables.dynamic.documentaryBonuses", { bonuses: bonuses.map((b) => `${b.label} +${b.pct}%`).join(" · ") }));
        }
    }
    const computedRetained = current.retained ?? (current.low !== null && current.high !== null
        ? Math.round((current.low + current.high) / 2)
        : current.low ?? current.high ?? null);
    const expertRetained = toNumber(retained);
    const shouldPreserveRetained = Boolean(preserveRetained &&
        expertRetained !== null &&
        current.low !== null &&
        current.high !== null &&
        expertRetained >= Math.min(current.low, current.high) &&
        expertRetained <= Math.max(current.low, current.high));
    return {
        ...current,
        retained: shouldPreserveRetained ? Math.round(expertRetained) : computedRetained,
        notes,
        details: {
            ...details,
            preservedExpertRetained: shouldPreserveRetained ? Math.round(expertRetained) : null,
        },
    };
}
function formatAdjustmentNotes(adjustments) {
    if (!adjustments?.notes?.length)
        return null;
    return adjustments.notes.join(' · ');
}
function methodAdjustmentRows(adjustments) {
    if (!adjustments?.details)
        return [];
    const rows = [];
    const condition = adjustments.details.conditionDiscount;
    if (condition) {
        rows.push({
            type: condition.pct > 0 ? 'Décote' : 'Neutre',
            label: condition.label || i18next.t("estimationSemblables.ui.etatDeConservation"),
            impact: condition.pct > 0 ? `−${condition.pct}%` : '0%',
        });
    }
    if (adjustments.details.prudenceDiscountPct > 0) {
        rows.push({
            type: 'Décote',
            label: i18next.t("estimationSemblables.ui.decotePrudentielleGroupeB"),
            impact: `−${adjustments.details.prudenceDiscountPct}%`,
        });
    }
    if (adjustments.details.liquidityDiscountPct > 0) {
        rows.push({
            type: 'Décote',
            label: i18next.t("estimationSemblables.ui.abattementDeLiquiditeGroupeC"),
            impact: `−${adjustments.details.liquidityDiscountPct}%`,
        });
    }
    if (Array.isArray(adjustments.details.documentaryBonuses)) {
        adjustments.details.documentaryBonuses.forEach((bonus) => {
            rows.push({
                type: 'Bonus',
                label: bonus.label,
                impact: `+${bonus.pct}%`,
            });
        });
    }
    if (!rows.length) {
        rows.push({
            type: 'Neutre',
            label: i18next.t("estimationSemblables.ui.aucunAjustementMethodologiqueApplicable"),
            impact: '0%',
        });
    }
    return rows;
}
function getGroupBSubLabel(prudencePct, hasOwnRecent, t) {
    const m = (key) => t(`estimationSemblables.methodology.groupB.${key}`);
    if (prudencePct === 0)
        return { code: 'B3', label: m('b3label'), desc: m('b3desc') };
    if (prudencePct <= 8 && hasOwnRecent)
        return { code: 'B1', label: m('b1label'), desc: m('b1desc') };
    return { code: 'B2', label: m('b2label'), desc: m('b2desc') };
}
function FinalMethodologyPanel({ title, adjustments, visible = true }) {
    const { t } = useTranslation();
    if (!visible || !adjustments)
        return null;
    const rows = methodAdjustmentRows(adjustments);
    const hasAdjustments = rows.some((row) => row.type !== 'Neutre');
    const prudencePct = adjustments.details?.prudenceDiscountPct ?? 0;
    const isGroupB = prudencePct > 0 || (adjustments.details?.segmentCounts && Object.keys(adjustments.details.segmentCounts).some(k => k !== 'primary'));
    const hasOwnRecent = prudencePct > 0 && prudencePct < 15;
    const subGroup = isGroupB ? getGroupBSubLabel(prudencePct, hasOwnRecent, t) : null;
    const baseEqualsValue = adjustments.details?.baseLow === adjustments.low &&
        adjustments.details?.baseHigh === adjustments.high;
    return (<div className="mt-4 border border-slate-200 bg-slate-50 rounded-lg p-4 text-xs text-slate-900">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="font-semibold">{title}</p>
        <span className={`px-2 py-1 rounded-full ${hasAdjustments ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-700'}`}>
          {hasAdjustments ? i18next.t("estimationSemblables.ui.ajustementsAppliques") : i18next.t("estimationSemblables.ui.aucunAjustement")}
        </span>
      </div>

      {/* Encart sous-groupe B — lisible pour le client */}
      {subGroup && (<div className="mb-3 border border-amber-200 bg-amber-50 rounded p-3">
          <p className="font-semibold text-amber-900 mb-1"><Trans i18nKey="estimationSemblables.ui.groupeB"/> {subGroup.code} — {subGroup.label}
          </p>
          <p className="text-amber-800 leading-relaxed">{subGroup.desc}</p>
        </div>)}

      <div className={`grid grid-cols-1 md:grid-cols-${baseEqualsValue ? '2' : '3'} gap-3 mb-3`}>
        <div className="bg-white border border-slate-200 rounded p-3">
          <p className="text-slate-500 uppercase tracking-wide text-[10px]"><Trans i18nKey="estimationSemblables.ui.baseBruteAvantDecotes"/></p>
          <p className="font-semibold mt-1">
            {adjustments.details?.baseLow != null
            ? `${formatEuro(adjustments.details.baseLow)} – ${formatEuro(adjustments.details.baseHigh ?? adjustments.details.baseLow)}`
            : '—'}
          </p>
        </div>

        {!baseEqualsValue && (<div className="bg-white border border-slate-200 rounded p-3">
            <p className="text-slate-500 uppercase tracking-wide text-[10px]"><Trans i18nKey="estimationSemblables.ui.valeurApresAjustements"/></p>
            <p className="font-semibold mt-1">
              {adjustments.low != null ? `${formatEuro(adjustments.low)} – ${formatEuro(adjustments.high ?? adjustments.low)}` : '—'}
            </p>
          </div>)}

        <div className="bg-white border border-slate-200 rounded p-3">
          <p className="text-slate-500 uppercase tracking-wide text-[10px]"><Trans i18nKey="estimationSemblables.ui.valeurRetenue"/></p>
          <p className="font-semibold mt-1">
            {adjustments.retained != null ? formatEuro(adjustments.retained) : '—'}
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 text-[10px] uppercase tracking-wide text-slate-500">
          <div className="col-span-3">Type</div>
          <div className="col-span-6">Ajustement</div>
          <div className="col-span-3 text-right">Impact</div>
        </div>

        {rows.map((row, index) => (<div key={`${row.label}-${index}`} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100">
            <div className={`col-span-3 font-medium ${row.type === 'Bonus'
                ? 'text-green-700'
                : row.type === 'Décote'
                    ? 'text-red-700'
                    : 'text-slate-600'}`}>
              {row.type === "Bonus" ? i18next.t("estimationSemblables.adjustmentType.bonus") : row.type === "Décote" ? i18next.t("estimationSemblables.adjustmentType.discount") : i18next.t("estimationSemblables.adjustmentType.neutral")}
            </div>
            <div className="col-span-6 text-slate-700">{row.label}</div>
            <div className={`col-span-3 text-right font-semibold ${row.type === 'Bonus'
                ? 'text-green-700'
                : row.type === 'Décote'
                    ? 'text-red-700'
                    : 'text-slate-600'}`}>
              {row.impact}
            </div>
          </div>))}
      </div>

      <p className="mt-3 text-slate-500"><Trans i18nKey="estimationSemblables.ui.ordreAppliqueEtatDeConservationDecotePrudentielleLiquiditeBonus"/> </p>
    </div>);
}
// ── Détection médium temporel (vidéo/film/installation) ─────────────────
function clampScore(value, min = 0, max = 100) {
    const n = toNumber(value);
    if (n === null)
        return min;
    return Math.max(min, Math.min(max, Math.round(n)));
}
function parseComparableDate(value) {
    if (!value)
        return null;
    if (value instanceof Date && !Number.isNaN(value.getTime()))
        return value;
    const raw = String(value).trim();
    const fr = raw.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.]((?:19|20)\d{2})$/);
    if (fr) {
        const [, dd, mm, yyyy] = fr;
        const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        return Number.isNaN(d.getTime()) ? null : d;
    }
    const iso = raw.match(/((?:19|20)\d{2})[/-](\d{1,2})[/-](\d{1,2})/);
    if (iso) {
        const [, yyyy, mm, dd] = iso;
        const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
}
const MEDIUM_CANONICAL_LABELS = new Set(['painting','sculpture','photography','drawing','print','mixed_media','installation','video','textile','ceramic','nft','other','oil_on_canvas','acrylic_on_canvas','ink_on_paper','watercolor_on_paper','pencil_on_paper','charcoal_on_paper','mixed_media_on_paper']);
function humanizeMedium(value) {
    const raw = String(value || '').trim();
    if (!raw)
        return '';
    const key = raw
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    if (MEDIUM_CANONICAL_LABELS.has(key))
        return i18next.t(`estimationSemblables.medium.${key}`);
    return raw.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}
function isTimeBasedMedium(medium) {
    if (!medium)
        return false;
    const m = String(medium).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return /video|film|installation|numerique|num\.rique|led|son|audio|performance|loop|boucle/.test(m);
}
function getMethodGroupLabel(methodGroup) {
    const key = String(methodGroup || '').trim();
    const known = new Set(['point_method','analogy_cup','direct_cup','direct_cup_prudent','analogy_cup_ratio','hybrid_points_cup','personal_quote_analogy_cup','personal_quote_direct_segment','group_b_analogy','group_b_hybrid']);
    return i18next.t(`estimationSemblables.methodGroup.${known.has(key) ? key : 'unknown'}`);
}
function getMicroSegmentLabel(quote) {
    const settings = quote?.direct_comparable_settings;
    if (!settings)
        return '';
    if (typeof settings === 'string') {
        try {
            return JSON.parse(settings)?.micro_segment || '';
        }
        catch {
            return '';
        }
    }
    return settings.micro_segment || '';
}
function isRecentComparable(comp, currentYear = new Date().getFullYear()) {
    const rawDate = comp?.date || comp?.auction_date || comp?.sale_date || comp?.created_at;
    const parsed = parseComparableDate(rawDate);
    if (!parsed)
        return false;
    const year = parsed.getFullYear();
    return Number.isFinite(year) && year >= currentYear - 3;
}
function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function getTargetArtistNameVariants(artistName) {
    const raw = String(artistName || '').trim();
    if (!raw)
        return [];
    const normalized = raw.replace(/\s+/g, ' ');
    const parts = normalized.split(' ').filter(Boolean);
    const variants = new Set([normalized]);
    // Cas fréquent : "Fabien VERSCHAERE" → supprimer aussi "Verschaere"
    // dans les commentaires d'analogie sans casser les comparables directs.
    if (parts.length >= 2) {
        variants.add(parts[parts.length - 1]);
    }
    return [...variants].filter((value) => value.length >= 3);
}
function stripTargetArtistMentions(text, targetArtistName) {
    if (!text)
        return text;
    let cleaned = String(text);
    const artistVariants = getTargetArtistNameVariants(targetArtistName);
    artistVariants.forEach((artistVariant) => {
        const artistRegex = escapeRegExp(artistVariant);
        cleaned = cleaned
            // Supprime les formulations qui rattachent artificiellement un résultat B à l'œuvre cible.
            .replace(new RegExp(`\\s*[–—-]?\\s*(analogue|analogie|comparable|comparaison|référence|reference)\\s+(directe?\\s+)?(pour|avec|à|a|de)?\\s*${artistRegex}[^.]*\\.?`, 'gi'), '')
            .replace(new RegExp(`\\s*[–—-]?\\s*${artistRegex}\\s*/\\s*analogue[^.]*\\.?`, 'gi'), '')
            .replace(new RegExp(`\\s*[–—-]?\\s*${artistRegex}\\s*[:;,-][^.]*\\.?`, 'gi'), '')
            // Nettoyage minimal de mentions résiduelles isolées.
            .replace(new RegExp(`\\b${artistRegex}\\b`, 'gi'), '');
    });
    return cleaned
        .replace(/\s+([.,;:])/g, '$1')
        .replace(/\s{2,}/g, ' ')
        .replace(/\s*[–—-]\s*\./g, '.')
        .trim();
}
function getComparableArtistDeathYear(comp = {}) {
    const raw = comp.artist_death_year ??
        comp.artistDeathYear ??
        comp.death_year ??
        comp.deathYear ??
        comp.artist_death_date ??
        comp.artistDeathDate ??
        comp.death_date ??
        comp.deathDate ??
        null;
    if (!raw)
        return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return raw >= 1000 && raw <= 3000 ? Math.round(raw) : null;
    }
    const year = String(raw).match(/\b(18|19|20)\d{2}\b/)?.[0];
    return year ? Number(year) : null;
}
function isArtistDeceasedInCurrentYear(comp, currentYear = new Date().getFullYear()) {
    const deathYear = getComparableArtistDeathYear(comp);
    return Boolean(deathYear && deathYear === currentYear);
}
function getArtistDeathMarketMessage(comp) {
    const deathYear = getComparableArtistDeathYear(comp);
    if (!deathYear)
        return null;
    return i18next.t("estimationSemblables.dynamic.recentArtistDeath", { year: deathYear });
}
function sanitizeComparableForArtwork(comp, targetArtwork) {
    if (!comp || typeof comp !== 'object')
        return comp;
    const targetArtistName = targetArtwork?.artist || '';
    const commentFields = [
        'comment',
        'comments',
        'note',
        'notes',
        'rationale',
        'reason',
        'analysis',
        'analysis_note',
        'analysisNote',
        'market_note',
        'marketNote',
        'methodology_note',
        'methodologyNote',
        'cup_note',
        'cupNote',
        'decision_note',
        'decisionNote',
    ];
    const cleaned = { ...comp };
    commentFields.forEach((field) => {
        if (typeof cleaned[field] === 'string') {
            cleaned[field] = stripTargetArtistMentions(cleaned[field], targetArtistName);
        }
    });
    const deathMessage = isArtistDeceasedInCurrentYear(cleaned)
        ? getArtistDeathMarketMessage(cleaned)
        : null;
    return {
        ...cleaned,
        // Normalise price : AutoCUP peut remonter hammer_price / estimated_value / selected_price
        // sans renseigner price. Sans cette normalisation, validComparables.filter(c => c.price)
        // exclut ces comparables et le bloc méthodologique affiche 0.
        price: cleaned.price ?? cleaned.hammer_price ?? cleaned.selected_price ?? cleaned.estimated_value ?? cleaned.estimate_mid ?? null,
        marketAlert: deathMessage || cleaned.marketAlert || null,
    };
}
function sanitizeComparableBatchForArtwork(comparables, targetArtwork) {
    if (!Array.isArray(comparables))
        return [];
    return comparables.map((comp) => sanitizeComparableForArtwork(comp, targetArtwork));
}
function comparableStableKey(comp = {}) {
    const normalizePart = (value) => String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    const price = comp.price ??
        comp.hammer_price ??
        comp.selected_price ??
        comp.estimated_value ??
        comp.estimate_mid ??
        '';
    const date = comp.date ??
        comp.auction_date ??
        comp.sale_date ??
        comp.auction_sale_date ??
        '';
    return [
        normalizePart(comp.artist || comp.artist_name || comp.artist_snapshot),
        normalizePart(comp.title || comp.title_snapshot),
        normalizePart(date),
        normalizePart(price),
    ].join('|');
}
function comparableCompletenessScore(comp = {}) {
    return [
        comp.id,
        comp.source_id,
        comp.comparable_id,
        comp.artist,
        comp.title,
        comp.material,
        comp.medium,
        comp.dimensions,
        comp.height_cm,
        comp.width_cm,
        comp.depth_cm,
        comp.auction_house,
        comp.source,
        comp.price,
        comp.hammer_price,
        comp.estimated_value,
        comp.estimate_low,
        comp.estimate_high,
        comp.url,
        comp.source_file,
        comp.comment,
    ].filter((value) => value !== null && value !== undefined && String(value).trim() !== '').length;
}
function mergeComparableRows(rows = [], maxRows = MAX_MANUAL_COMPARABLES) {
    const merged = new Map();
    const order = [];
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        if (!row || typeof row !== 'object')
            return;
        const hasContent = row.title || row.artist || row.price || row.hammer_price || row.estimated_value || row.source || row.auction_house || row.date || row.auction_date;
        if (!hasContent)
            return;
        const key = comparableStableKey(row);
        if (!key || key === '|||')
            return;
        const existing = merged.get(key);
        if (!existing) {
            merged.set(key, row);
            order.push(key);
            return;
        }
        const best = comparableCompletenessScore(row) > comparableCompletenessScore(existing)
            ? { ...existing, ...row }
            : { ...row, ...existing };
        merged.set(key, best);
    });
    return order.map((key) => merged.get(key)).slice(0, maxRows);
}
function isUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || '').trim());
}
function getExistingComparableId(comp = {}) {
    const candidates = [comp.comparable_id, comp.source_id, comp.id];
    return candidates.find((value) => isUuid(value)) || null;
}
function normalizeComparableDateForDB(value) {
    if (!value)
        return null;
    const parsed = parseComparableDate(value);
    if (!parsed)
        return null;
    return parsed.toISOString().slice(0, 10);
}
function getComparableSelectedPrice(comp = {}) {
    return toNumber(comp.selected_price ??
        comp.price ??
        comp.hammer_price ??
        comp.estimated_value ??
        comp.estimate_mid);
}
function emptyComparableRow() {
    return { title: '', artist: '', source: '', price: '', date: '', url: '' };
}
function getMethodologySignal({ artistQuote, comparables, confidence, t }) {
    const methodGroup = artistQuote?.method_group;
    const currentYear = new Date().getFullYear();
    const allComparables = Array.isArray(comparables) ? comparables : [];
    const completeComparables = allComparables.filter((c) => c?.title && c?.artist && c?.price);
    const recentComparablesCount = completeComparables.filter((c) => isRecentComparable(c, currentYear)).length;
    const totalComparablesCount = completeComparables.length;
    const confidenceValue = toNumber(confidence);
    const m = (key) => t(`estimationSemblables.methodology.${key}`);
    if (!artistQuote) {
        return {
            recentComparablesCount,
            totalComparablesCount,
            title: i18next.t("estimationSemblables.ui.methodologieADefinir"),
            tone: "amber",
            badge: m("toDefine.badge"),
            socle: m("toDefine.socle"),
            signal: m("toDefine.signal"),
            adjustment: m("toDefine.adjustment"),
            conclusion: m("toDefine.conclusion"),
        };
    }
    if (isGroupCMethodGroup(methodGroup)) {
        return {
            recentComparablesCount,
            totalComparablesCount,
            title: i18next.t("estimationSemblables.ui.lectureMethodologiqueArtkiv"),
            tone: "purple",
            badge: m("pointMethod.badge"),
            socle: m("pointMethod.socle"),
            signal: m("pointMethod.signal"),
            adjustment: m("pointMethod.adjustment"),
            conclusion: m("pointMethod.conclusion"),
        };
    }
    if (methodGroup === "analogy_cup") {
        if (recentComparablesCount >= 5 && confidenceValue !== null && confidenceValue >= 70) {
            return {
                recentComparablesCount,
                totalComparablesCount,
                title: i18next.t("estimationSemblables.ui.lectureMethodologiqueArtkiv"),
                tone: "green",
                badge: m("quasiDirectCup.badge"),
                socle: m("quasiDirectCup.socle"),
                signal: i18next.t("estimationSemblables.dynamic.ownRecentResults", { count: recentComparablesCount }),
                adjustment: m("quasiDirectCup.adjustment"),
                conclusion: m("quasiDirectCup.conclusion"),
            };
        }
        if (recentComparablesCount >= 2) {
            return {
                recentComparablesCount,
                totalComparablesCount,
                title: i18next.t("estimationSemblables.ui.lectureMethodologiqueArtkiv"),
                tone: "blue",
                badge: m("hybridReinforced.badge"),
                socle: m("hybridReinforced.socle"),
                signal: i18next.t("estimationSemblables.dynamic.recentImported", { count: recentComparablesCount }),
                adjustment: m("hybridReinforced.adjustment"),
                conclusion: m("hybridReinforced.conclusion"),
            };
        }
        return {
            recentComparablesCount,
            totalComparablesCount,
            title: i18next.t("estimationSemblables.ui.lectureMethodologiqueArtkiv"),
            tone: "amber",
            badge: m("analogyPrudent.badge"),
            socle: m("analogyPrudent.socle"),
            signal: recentComparablesCount === 1
                ? m("analogyPrudent.signalOne")
                : m("analogyPrudent.signalNone"),
            adjustment: m("analogyPrudent.adjustment"),
            conclusion: m("analogyPrudent.conclusion"),
        };
    }
    if (methodGroup === "direct_cup") {
        return {
            recentComparablesCount,
            totalComparablesCount,
            title: i18next.t("estimationSemblables.ui.lectureMethodologiqueArtkiv"),
            tone: recentComparablesCount >= 3 ? "green" : "blue",
            badge: recentComparablesCount >= 3 ? m("directCupActive.badge") : m("directCupIncomplete.badge"),
            socle: m("directCupActive.socle"),
            signal: i18next.t("estimationSemblables.dynamic.recentDetectedThreeYears", { count: recentComparablesCount }),
            adjustment: m("directCupActive.adjustment"),
            conclusion: m("directCupActive.conclusion"),
        };
    }
    return {
        recentComparablesCount,
        totalComparablesCount,
        title: i18next.t("estimationSemblables.ui.lectureMethodologiqueArtkiv"),
        tone: "blue",
        badge: m("dynamic.badge"),
        socle: getMethodGroupLabel(methodGroup),
        signal: i18next.t("estimationSemblables.dynamic.recentDetected", { count: recentComparablesCount }),
        adjustment: m("dynamic.adjustment"),
        conclusion: m("dynamic.conclusion"),
    };
}
function getMethodologyToneClasses(tone) {
    if (tone === 'green') {
        return {
            box: 'border-green-200 bg-green-50',
            title: 'text-green-950',
            text: 'text-green-900',
            muted: 'text-green-800',
            badge: 'bg-green-100 text-green-800',
            line: 'border-green-200',
        };
    }
    if (tone === 'purple') {
        return {
            box: 'border-purple-200 bg-purple-50',
            title: 'text-purple-950',
            text: 'text-purple-900',
            muted: 'text-purple-800',
            badge: 'bg-purple-100 text-purple-800',
            line: 'border-purple-200',
        };
    }
    if (tone === 'amber') {
        return {
            box: 'border-amber-200 bg-amber-50',
            title: 'text-amber-950',
            text: 'text-amber-900',
            muted: 'text-amber-800',
            badge: 'bg-amber-100 text-amber-800',
            line: 'border-amber-200',
        };
    }
    return {
        box: 'border-blue-200 bg-blue-50',
        title: 'text-blue-950',
        text: 'text-blue-900',
        muted: 'text-blue-800',
        badge: 'bg-blue-100 text-blue-800',
        line: 'border-blue-200',
    };
}
function MethodologyReadingCard({ signal, confidence }) {
    if (!signal)
        return null;
    const classes = getMethodologyToneClasses(signal.tone);
    return (<div className={`mt-4 rounded-lg border p-4 ${classes.box}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className={`text-sm font-semibold ${classes.title}`}><Trans i18nKey="estimationSemblables.ui.lectureMethodologiqueArtkiv"/> </h3>
        <span className={`text-xs px-3 py-1 rounded-full ${classes.badge}`}>
          {signal.badge}
        </span>
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 text-xs ${classes.text}`}>
        <div className={`rounded border ${classes.line} bg-white/50 p-3`}>
          <p className="font-medium mb-1"><Trans i18nKey="estimationSemblables.ui.socleMethodologique"/></p>
          <p className={classes.muted}>{signal.socle}</p>
        </div>

        <div className={`rounded border ${classes.line} bg-white/50 p-3`}>
          <p className="font-medium mb-1"><Trans i18nKey="estimationSemblables.ui.signalMarcheRecent"/></p>
          <p className={classes.muted}>{signal.signal}</p>
        </div>

        <div className={`rounded border ${classes.line} bg-white/50 p-3`}>
          <p className="font-medium mb-1"><Trans i18nKey="estimationSemblables.ui.ajustementDynamique"/></p>
          <p className={classes.muted}>{signal.adjustment}</p>
        </div>

        <div className={`rounded border ${classes.line} bg-white/50 p-3`}>
          <p className="font-medium mb-1"><Trans i18nKey="estimationSemblables.ui.conclusionMoteur"/></p>
          <p className={classes.muted}>{signal.conclusion}</p>
        </div>
      </div>

      <div className={`mt-3 text-xs ${classes.muted}`}><Trans i18nKey="estimationSemblables.ui.comparablesComplets"/> <strong>{signal.totalComparablesCount}</strong>
        {' '}<Trans i18nKey="estimationSemblables.ui.recents"/> <strong>{signal.recentComparablesCount}</strong>
        {confidence !== null && confidence !== undefined ? <><Trans i18nKey="estimationSemblables.ui.confiance"/> <strong>{clampScore(confidence)}/100</strong></> : null}
      </div>
    </div>);
}
function buildPointMethodSuggestion(artwork, quote) {
    if (!quote || !isGroupCMethodGroup(quote.method_group))
        return null;
    const h = getArtworkHeight(artwork);
    const w = getArtworkWidth(artwork);
    const timeBased = isTimeBasedMedium(artwork.medium || artwork.typeOfArt);
    // Taux depuis la cotation (bas / haut)
    const pointLow = toNumber(quote.point_rate_low_eur ?? quote.point_rate_raw_eur);
    const pointHigh = toNumber(quote.point_rate_high_eur ?? quote.recommended_point_rate_eur ?? quote.point_rate_raw_eur);
    // ── Calcul principal : (H + L) × taux ─────────────────────────────────
    const formulaLow = h && w && pointLow ? Math.round((h + w) * pointLow) : null;
    const formulaHigh = h && w && pointHigh ? Math.round((h + w) * pointHigh) : null;
    // ── Override / forfait expert ────────────────────────────────────────────
    // override_low/high = champ dédié (futur)
    // price_low/price_high de la cotation = forfait quand pas de taux (ex: Dreyfus, Tremblay)
    const overrideLow = toNumber(quote.override_low ?? null);
    const overrideHigh = toNumber(quote.override_high ?? null);
    // Forfait depuis price_low/price_high de la cotation (uniquement si pas de taux)
    const forfaitLow = (!pointLow && !pointHigh) ? toNumber(quote.price_low ?? null) : null;
    const forfaitHigh = (!pointLow && !pointHigh) ? toNumber(quote.price_high ?? null) : null;
    // Base avant décote : formule > override > forfait cotation
    const baseLow = formulaLow ?? overrideLow ?? forfaitLow ?? null;
    const baseHigh = formulaHigh ?? overrideHigh ?? forfaitHigh ?? baseLow ?? null;
    // ── Décote conservation ───────────────────────────────────────────────
    const conditionDiscount = getConditionDiscount(artwork.condition);
    const conditionFactor = (100 - conditionDiscount.pct) / 100;
    const finalAdjustments = applyFinalMethodologyAdjustments({
        low: baseLow,
        high: baseHigh,
        retained: baseLow && baseHigh ? Math.round((baseLow + baseHigh) / 2) : baseLow ?? baseHigh ?? null,
        artwork,
        artistQuote: quote,
        methodGroup: 'point_method',
        includeCondition: true,
        includeDocumentaryBonuses: true,
        includePrudence: false,
        includeLiquidity: true,
    });
    const low = finalAdjustments.low;
    const high = finalAdjustments.high;
    const retained = finalAdjustments.retained;
    // Cas irréparable : write-off — on retourne un objet explicite plutôt que null
    if (conditionDiscount.pct === 100) {
        return {
            method: 'point_method',
            isWriteOff: true,
            low: 0, high: 0, retained: 0,
            baseLow, baseHigh,
            conditionDiscount,
            finalAdjustments,
            height: h, width: w,
            hasDimensions: Boolean(h && w),
            isForfait: false, isOverride: false,
            quoteId: quote.id,
            summary: i18next.t("estimationSemblables.ui.irreparableValeurNulleDepreciationTotale"),
        };
    }
    if (!low && !high && !retained)
        return null;
    // ── Drapeaux pour l'affichage ──────────────────────────────────────────
    const hasDimensions = Boolean(h && w);
    // Vidéo/installation = forfait obligatoire (pas de formule dimensionnelle)
    const isForfait = Boolean(timeBased || (!pointLow && !pointHigh && (forfaitLow || forfaitHigh)));
    const isOverride = Boolean(overrideLow || overrideHigh);
    return {
        method: 'point_method',
        low,
        high,
        retained,
        baseLow,
        baseHigh,
        pointLow,
        pointHigh,
        height: h,
        width: w,
        formulaLow,
        formulaHigh,
        overrideLow,
        overrideHigh,
        forfaitLow,
        forfaitHigh,
        isOverride,
        isForfait,
        timeBased,
        hasDimensions,
        conditionDiscount,
        conditionFactor,
        finalAdjustments,
        quoteId: quote.id,
        confidence: quote.confidence || 'med',
        summary: isForfait
            ? i18next.t("estimationSemblables.dynamic.pointFlatRateSummary", { low: formatEuro(low), high: formatEuro(high) })
            : hasDimensions
                ? i18next.t("estimationSemblables.dynamic.pointFormulaSummary", { height: h, width: w, lowRate: pointLow ?? "?", highRate: pointHigh ?? "?", condition: conditionDiscount.pct ? ` · ${conditionDiscount.label}` : "" })
                : i18next.t("estimationSemblables.dynamic.pointExpertFlatRateSummary", { low: formatEuro(low), high: formatEuro(high) }),
    };
}
const METHOD_GROUP_LABELS = new Set(['direct_cup', 'analogy_cup', 'point_method', 'points', 'forfait']);
function getHumanMethodLabel(methodKey) {
    if (!methodKey) return null;
    return METHOD_GROUP_LABELS.has(methodKey)
        ? i18next.t(`estimationSemblables.humanMethod.${methodKey}`)
        : String(methodKey).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}
function buildExpertMethodologySnapshot({ selectedDecisions = [], manualComparables = [], autoCupComparables = [], quoteSuggestion = null, artistQuote = null, artwork = null, confidenceScore = null }) {
    const savedRows = Array.isArray(selectedDecisions) ? selectedDecisions : [];
    const typedComparables = Array.isArray(manualComparables) ? manualComparables : [];
    const referenceArtist = normalizeKey(artwork?.artist || artistQuote?.artist || '');
    const manualRows = typedComparables
        .filter((c) => c?.title && c?.artist && toNumber(c?.price) !== null)
        .map((c, index) => {
        const sameArtist = normalizeKey(c.artist) === referenceArtist;
        return {
            id: `manual-${index}`,
            kind: sameArtist ? 'direct_sale' : 'analogy',
            selected_price: toNumber(c.price),
            note: sameArtist ? i18next.t("estimationSemblables.snapshot.manualDirect") : i18next.t("estimationSemblables.snapshot.manualAnalogy"),
            artist_snapshot: c.artist,
            title_snapshot: c.title,
            source_snapshot: c.source,
        };
    });
    // Comparables AutoCUP — enrichissent les compteurs même sans "Ajouter à l'estimation"
    const autoCupRows = Array.isArray(autoCupComparables)
        ? autoCupComparables
            .filter((c) => toNumber(c?.price || c?.hammer_price || c?.estimated_value) !== null)
            .map((c, index) => {
            const sameArtist = normalizeKey(c.artist || '') === referenceArtist;
            const isAnalog = !sameArtist || c.is_analog_comparable;
            return {
                id: `autocup-${index}`,
                kind: isAnalog ? 'analogy' : 'direct_sale',
                selected_price: toNumber(c.price || c.hammer_price || c.estimated_value),
                note: isAnalog ? i18next.t("estimationSemblables.snapshot.autoAnalogy") : i18next.t("estimationSemblables.snapshot.autoDirect"),
                artist_snapshot: c.artist,
                title_snapshot: c.title,
            };
        })
        : [];
    // Cumule toutes les sources disponibles et déduplique par id.
    // Évite l'affichage à 0 avant qu'AutoCUP ait fini de charger :
    // savedDecisions est disponible immédiatement depuis la BDD,
    // autoCupRows s'enrichit ensuite quand onMethodologyComparablesChange se déclenche.
    const allRows = [...savedRows, ...manualRows, ...autoCupRows];
    const rows = allRows.filter((r, i, arr) => arr.findIndex((x) => (x.comparable_id || x.id) &&
        (x.comparable_id || x.id) === (r.comparable_id || r.id)) === i);
    const directSales = rows.filter((r) => r.kind === 'direct_sale').length;
    const extrapolations = rows.filter((r) => r.kind === 'extrapolation').length;
    const analogies = rows.filter((r) => r.kind === 'analogy' || String(r.note || '').toLowerCase().includes('analogie') || r.ratio_id).length;
    const marketDepth = directSales + analogies;
    let group = 'group_c';
    if (artistQuote?.method_group === 'direct_cup')
        group = 'group_a';
    else if (artistQuote?.method_group === 'analogy_cup')
        group = 'group_b';
    else if (isGroupCMethodGroup(artistQuote?.method_group))
        group = 'group_c';
    else if (directSales >= 5)
        group = 'group_a';
    else if (directSales >= 2 || analogies > 0)
        group = 'group_b';
    const confidence = clampScore(Number.isFinite(Number(confidenceScore))
        ? Number(confidenceScore)
        : group === 'group_a' ? 75 : group === 'group_b' ? 52 : 35);
    const summaryByGroup = {
        group_a: i18next.t("estimationSemblables.snapshot.summaryA", { direct: directSales }),
        group_b: i18next.t("estimationSemblables.snapshot.summaryB", { direct: directSales, analogies, extrapolations }),
        group_c: i18next.t("estimationSemblables.snapshot.summaryC"),
    };
    const methodLabel = getHumanMethodLabel(artistQuote?.method_group);
    const expertReasoning = [
        methodLabel ? i18next.t("estimationSemblables.snapshot.sourceMethod", { method: methodLabel }) : null,
        quoteSuggestion?.summary || null,
        extrapolations ? i18next.t("estimationSemblables.snapshot.extrapolations", { count: extrapolations }) : null,
        directSales < 5 ? i18next.t("estimationSemblables.snapshot.depthWarning") : null,
    ].filter(Boolean).join(' ');
    return {
        methodology_group: group,
        market_depth: marketDepth,
        direct_sales_count: directSales,
        extrapolation_count: extrapolations,
        analogy_count: analogies,
        confidence_score: confidence,
        methodology_summary: summaryByGroup[group],
        expert_reasoning: expertReasoning,
    };
}
function ExpertMethodologyMemory({ artwork, estimationId, currentUserId, selectedDecisions = [], manualComparables = [], autoCupComparables = [], quoteSuggestion = null, artistQuote = null, confidenceScore = null, }) {
    const { t } = useTranslation();
    const [methodology, setMethodology] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const snapshot = useMemo(() => buildExpertMethodologySnapshot({
        selectedDecisions,
        manualComparables,
        autoCupComparables,
        quoteSuggestion,
        artistQuote,
        artwork,
        confidenceScore,
    }), [selectedDecisions, manualComparables, autoCupComparables, quoteSuggestion, artistQuote, artwork, confidenceScore]);
    useEffect(() => {
        let cancelled = false;
        async function loadMethodology() {
            if (!estimationId || !currentUserId)
                return;
            const { data, error: loadError } = await supabase
                .from('estimation_methodology')
                .select('*')
                .eq('estimation_id', estimationId)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
            if (cancelled)
                return;
            if (loadError) {
                setError(loadError.message);
                return;
            }
            setMethodology(data || null);
        }
        loadMethodology();
        return () => {
            cancelled = true;
        };
    }, [estimationId, currentUserId]);
    async function saveMethodology() {
        if (!estimationId || !currentUserId) {
            setError(t('estimationSemblables.save.missingId'));
            return;
        }
        setSaving(true);
        setError('');
        const payload = {
            estimation_id: estimationId,
            ...snapshot,
            expert_override: true,
            generated_by_ai: false,
            created_by: currentUserId,
        };
        try {
            const { data: existing, error: existingError } = await runWithAbortRetry(() => supabase
                .from('estimation_methodology')
                .select('id')
                .eq('estimation_id', estimationId)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle(), { retries: 2, label: i18next.t("estimationSemblables.ui.lectureJustificationMarche") });
            if (existingError)
                throw existingError;
            const saveRequest = () => existing?.id
                ? supabase.from('estimation_methodology').update(payload).eq('id', existing.id).select('*').single()
                : supabase.from('estimation_methodology').insert(payload).select('*').single();
            const { data, error: saveError } = await runWithAbortRetry(saveRequest, { retries: 2, label: i18next.t("estimationSemblables.ui.sauvegardeJustificationMarche") });
            if (saveError)
                throw saveError;
            setMethodology(data);
        }
        catch (saveError) {
            if (isAbortLikeError(saveError)) {
                console.warn('Justification : requête annulée par le navigateur après retry. Non bloquant.', saveError?.message || saveError);
                setError('');
                return;
            }
            setError(saveError?.message || String(saveError));
        }
        finally {
            setSaving(false);
        }
    }
    const active = methodology || snapshot;
    const label = active.methodology_group === 'group_a'
        ? i18next.t("estimationSemblables.marketStrength.strong")
        : active.methodology_group === 'group_b'
            ? i18next.t("estimationSemblables.marketStrength.limited")
            : i18next.t("estimationSemblables.marketStrength.scarce");
    return (<div className="border border-amber-200 bg-amber-50 rounded-lg p-4 mb-4">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold"><Trans i18nKey="estimationSemblables.ui.lectureExpertDuMarche"/></p>
          <h3 className="text-base font-semibold text-gray-950 mt-1">{label}</h3>
          <p className="text-sm text-gray-700 mt-2">{active.methodology_summary}</p>
          {active.expert_reasoning && (<p className="text-xs text-gray-600 mt-2">{active.expert_reasoning}</p>)}
          <p className="text-[11px] text-gray-500 mt-3"><Trans i18nKey="estimationSemblables.ui.ventesDirectes"/> {active.direct_sales_count || 0}<Trans i18nKey="estimationSemblables.ui.extrapolations"/> {active.extrapolation_count || 0}<Trans i18nKey="estimationSemblables.ui.analogies"/> {active.analogy_count || 0}<Trans i18nKey="estimationSemblables.ui.confiance"/> {clampScore(active.confidence_score || 0)}/100
          </p>
        </div>
        <button type="button" onClick={saveMethodology} disabled={saving} className="px-4 py-2 rounded bg-black text-white text-xs disabled:bg-gray-300">
          {saving ? i18next.t("estimationSemblables.ui.enregistrement") : i18next.t("estimationSemblables.ui.enregistrerLaJustification")}
        </button>
      </div>
      {error && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-3">{error}</p>}
      <p className="text-[11px] text-amber-800 mt-3"><Trans i18nKey="estimationSemblables.ui.lesExtrapolationsEtAnalogiesConstituentDesOutilsDAide"/> </p>
    </div>);
}
function ExpertSelectedComparablesMemory({ selectedDecisions = [], onRemoveDecision = null }) {
    const { t } = useTranslation();
    const rows = Array.isArray(selectedDecisions) ? selectedDecisions : [];
    const prices = rows
        .map((row) => toNumber(row.selected_price))
        .filter((value) => value !== null && value > 0)
        .sort((a, b) => a - b);
    const directRows = rows.filter((row) => row.kind === 'direct_sale');
    const extrapolationRows = rows.filter((row) => row.kind === 'extrapolation');
    const low = prices.length ? prices[0] : null;
    const high = prices.length ? prices[prices.length - 1] : null;
    const median = prices.length
        ? prices.length % 2
            ? prices[Math.floor(prices.length / 2)]
            : Math.round((prices[prices.length / 2 - 1] + prices[prices.length / 2]) / 2)
        : null;
    const formatDate = (value) => {
        if (!value)
            return '—';
        const d = new Date(value);
        if (Number.isNaN(d.getTime()))
            return String(value).slice(0, 10);
        return d.toLocaleDateString(getLocaleCode());
    };
    const DecisionRow = ({ row }) => {
        const label = row.kind === 'extrapolation' ? i18next.t("estimationSemblables.memory.extrapolation") : i18next.t("estimationSemblables.memory.retainedSale");
        const title = row.title_snapshot || (row.kind === 'extrapolation' ? i18next.t("estimationSemblables.memory.untitledExtrapolation") : i18next.t("estimationSemblables.memory.untitledComparable"));
        const artist = row.artist_snapshot || i18next.t("estimationSemblables.memory.artistMissing");
        const source = row.source_snapshot || (row.ratio_id ? 'artist_market_ratio' : i18next.t("estimationSemblables.memory.sourceMissing"));
        return (<div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 border border-gray-200 bg-white rounded-lg p-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[11px] px-2 py-1 rounded-full ${row.kind === 'extrapolation' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
              {label}
            </span>
            <span className="text-[11px] text-gray-500"><Trans i18nKey="estimationSemblables.ui.enregistreLe"/> {formatDate(row.created_at)}</span>
          </div>
          <p className="font-medium text-gray-950 mt-1 truncate">{title}</p>
          <p className="text-xs text-gray-600 mt-0.5">{artist} · {source}</p>
          {row.note && (<p className="text-xs text-gray-500 mt-2 line-clamp-2">{row.note}</p>)}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-base font-semibold text-gray-950">{formatEuro(row.selected_price)}</p>
            {row.weight !== null && row.weight !== undefined && (<p className="text-[11px] text-gray-500">{i18next.t("estimationSemblables.memory.weight", { value: Number(row.weight).toLocaleString(getLocaleCode()) })}</p>)}
          </div>
          {onRemoveDecision && row.id && !String(row.id).startsWith('local-') && (<button type="button" onClick={() => onRemoveDecision(row.id)} className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50">
              {i18next.t("estimationSemblables.memory.remove")}
            </button>)}
        </div>
      </div>);
    };
    return (<div className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-4">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold"><Trans i18nKey="estimationSemblables.ui.memoireExpertDeCetteEstimation"/></p>
          <h3 className="text-base font-semibold text-gray-950 mt-1">
            {rows.length ? i18next.t("estimationSemblables.dynamic.savedChoices", { count: rows.length }) : i18next.t("estimationSemblables.ui.aucunChoixExpertEnregistre")}
          </h3>
          <p className="text-sm text-gray-700 mt-2"><Trans i18nKey="estimationSemblables.ui.ceBlocRecapituleLesVentesEtExtrapolationsReellementRetenues"/> </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center shrink-0">
          <div className="bg-white rounded border border-blue-100 px-3 py-2">
            <p className="text-[11px] text-gray-500">{i18next.t("estimationSemblables.memory.low")}</p>
            <p className="text-sm font-semibold">{formatEuro(low)}</p>
          </div>
          <div className="bg-white rounded border border-blue-100 px-3 py-2">
            <p className="text-[11px] text-gray-500"><Trans i18nKey="estimationSemblables.ui.mediane"/></p>
            <p className="text-sm font-semibold">{formatEuro(median)}</p>
          </div>
          <div className="bg-white rounded border border-blue-100 px-3 py-2">
            <p className="text-[11px] text-gray-500">{i18next.t("estimationSemblables.memory.high")}</p>
            <p className="text-sm font-semibold">{formatEuro(high)}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
        <span className="px-2 py-1 rounded bg-white border border-blue-100 text-gray-700"><Trans i18nKey="estimationSemblables.ui.ventesDirectes"/> {directRows.length}</span>
        <span className="px-2 py-1 rounded bg-white border border-blue-100 text-gray-700"><Trans i18nKey="estimationSemblables.ui.extrapolationse5783c"/> {extrapolationRows.length}</span>
        <span className="px-2 py-1 rounded bg-white border border-blue-100 text-gray-700"><Trans i18nKey="estimationSemblables.ui.fourchetteMemoire"/> {low && high ? `${formatEuro(low)} – ${formatEuro(high)}` : '—'}</span>
      </div>

      {rows.length ? (<div className="space-y-2">
          {rows.map((row) => <DecisionRow key={row.id || `${row.kind}-${row.created_at}-${row.selected_price}`} row={row}/>)}
        </div>) : (<div className="border border-dashed border-blue-200 bg-white rounded-lg p-4 text-sm text-gray-600">
          {t('estimationSemblables.memory.addFromCup')}
        </div>)}
    </div>);
}
export default function EstimationSemblablesPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { artworkId } = useParams();
    const { t } = useTranslation();
    const [artwork, setArtwork] = useState(null);
    const [userRole, setUserRole] = useState('user');
    const [isSharedArtwork, setIsSharedArtwork] = useState(false); // true = expert sur oeuvre partagee, update via RPC
    const [subscriptionLevel, setSubscriptionLevel] = useState('standard');
    const [visionLoading, setVisionLoading] = useState(false);
    const [visionResult, setVisionResult] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [artistQuote, setArtistQuote] = useState(null);
    const [artistQuoteOptions, setArtistQuoteOptions] = useState([]);
    const [selectedArtistQuoteId, setSelectedArtistQuoteId] = useState('');
    const [artistQuoteLoading, setArtistQuoteLoading] = useState(false);
    const [artistQuoteError, setArtistQuoteError] = useState(null);
    const [quoteSuggestion, setQuoteSuggestion] = useState(null);
    const [galleryCap, setGalleryCap] = useState(''); // borne haute galerie Groupe C/A/B (Artsy, Artsper…)
    // 'cup' = fourchette calculée via comparables AutoCUP
    // 'personal' = cotation personnelle expert (override direct)
    const [valuationMode, setValuationMode] = useState('cup');
    // Données comparables
    const [externalComparables, setExternalComparables] = useState([emptyComparableRow()]);
    const [selectedDecisions, setSelectedDecisions] = useState([]);
    // Comparables bruts remontés par AutoCUP via onMethodologyComparablesChange
    // Séparés de validComparables (manuels) pour alimenter correctement ExpertMethodologyMemory
    const [autoCupRawComparables, setAutoCupRawComparables] = useState([]);
    const [autoCupMeta, setAutoCupMeta] = useState({ hasOwnRecent: false, hasPrimarySegment: false });
    const [resolvedEstimationId, setResolvedEstimationId] = useState(null);
    const [estimationResolveError, setEstimationResolveError] = useState('');
    // ── Application au lot (sérigraphies, multiples, séries) ────────────────
    // Les autres œuvres du lot (hors œuvre courante), chargées avec leur condition
    // et leurs bonifications pour calculer la décote individuelle.
    const [lotMembers, setLotMembers] = useState([]);
    const [lotMembersLoading, setLotMembersLoading] = useState(false);
    const [lotApplyOpen, setLotApplyOpen] = useState(false);
    const [lotApplying, setLotApplying] = useState(false);
    const [lotApplyResult, setLotApplyResult] = useState(null); // { success, errors }
    // Métadonnées du lot (title, total_copies, edition_info) pour affichage du badge
    const [lotInfo, setLotInfo] = useState(null);
    // Write-off : mode confirmation + note obligatoire
    const [writeOffMode, setWriteOffMode] = useState(false);
    const [writeOffNote, setWriteOffNote] = useState('');
    const [expertFreeNote, setExpertFreeNote] = useState(''); // note libre de l'expert (CUP directe / AutoCUP)
    const [writeOffSaving, setWriteOffSaving] = useState(false);
    async function removeSelectedDecision(decisionId) {
        if (!decisionId)
            return;
        const previous = selectedDecisions;
        setSelectedDecisions((prev) => prev.filter((row) => row.id !== decisionId));
        const { error } = await supabase
            .from('estimation_selected_comparables')
            .delete()
            .eq('id', decisionId);
        if (error) {
            console.warn('Suppression comparable retenu:', error.message);
            setSelectedDecisions(previous);
        }
    }
    const canUseExpertVision = (userRole === 'expert' && subscriptionLevel === 'art_advisor_premium') ||
        userRole === 'admin' ||
        userRole === 'team';
    const canUseAutoCUP = canUseExpertVision;
    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artworkId]);
    useEffect(() => {
        // Une fiche œuvre = son propre panier local.
        // Évite que les comparables AutoCUP de la fiche précédente se promènent
        // dans la fiche suivante sous forme de doublons appauvris.
        setExternalComparables([emptyComparableRow()]);
        setQuoteSuggestion(null);
    }, [artworkId]);
    useEffect(() => {
        let cancelled = false;
        async function resolveOrCreateEstimationId() {
            setEstimationResolveError('');
            setResolvedEstimationId(null);
            if (!artwork?.id || !currentUserId)
                return;
            try {
                const candidateIds = [
                    artwork?.estimation_id,
                    artwork?.estimationId,
                    location?.state?.estimationId,
                    location?.state?.estimation_id,
                ].filter(Boolean);
                for (const candidateId of candidateIds) {
                    const { data, error } = await supabase
                        .from('estimations')
                        .select('id')
                        .eq('id', candidateId)
                        .maybeSingle();
                    if (error)
                        throw error;
                    if (data?.id) {
                        if (!cancelled)
                            setResolvedEstimationId(data.id);
                        return;
                    }
                }
                const { data: byArtwork, error: byArtworkError } = await supabase
                    .from('estimations')
                    .select('id, date')
                    .eq('artwork_id', artwork.id)
                    .order('date', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                if (byArtworkError)
                    throw byArtworkError;
                if (byArtwork?.id) {
                    if (!cancelled)
                        setResolvedEstimationId(byArtwork.id);
                    return;
                }
                // Dernier filet : créer le dossier d'estimation minimal.
                // estimations.client_id référence auth.users, donc currentUserId est le choix sûr.
                const { data: created, error: createError } = await supabase
                    .from('estimations')
                    .insert({
                    artwork_id: artwork.id,
                    estimated_value: 0,
                    client_id: currentUserId,
                    method: 'Estimation semblables',
                    notes: "Créée automatiquement pour mémoriser les comparables retenus par l'expert.",
                    estimation_date: new Date().toISOString().slice(0, 10),
                })
                    .select('id')
                    .single();
                if (createError)
                    throw createError;
                if (!cancelled)
                    setResolvedEstimationId(created.id);
            }
            catch (err) {
                if (!cancelled) {
                    setEstimationResolveError(err?.message || t('estimationSemblables.save.resolveFailed'));
                }
            }
        }
        resolveOrCreateEstimationId();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artwork?.id, artwork?.estimation_id, artwork?.estimationId, currentUserId, location?.state?.estimationId, location?.state?.estimation_id]);
    useEffect(() => {
        let cancelled = false;
        async function loadSelectedDecisions() {
            const estimationId = resolvedEstimationId;
            if (!estimationId || !currentUserId) {
                setSelectedDecisions([]);
                return;
            }
            // Étape 1 : charger les comparables retenus
            const { data, error } = await supabase
                .from('estimation_selected_comparables')
                .select('*')
                .eq('estimation_id', estimationId)
                .order('created_at', { ascending: false });
            if (cancelled)
                return;
            if (error) {
                console.warn('Chargement comparables retenus:', error.message);
                setSelectedDecisions([]);
                return;
            }
            const rows = data || [];
            setSelectedDecisions(rows);
            // Étape 2 : récupérer auction_date et source_file depuis estimation_comparables
            // via une requête séparée (le join Supabase peut être bloqué par RLS selon la visibilité).
            const comparableIds = rows
                .map((r) => r.comparable_id)
                .filter(Boolean);
            let comparablesByid = {};
            if (comparableIds.length) {
                const { data: compData } = await supabase
                    .from('estimation_comparables')
                    .select('id, auction_date, source_file, source')
                    .in('id', comparableIds);
                if (compData) {
                    comparablesByid = Object.fromEntries(compData.map((c) => [c.id, c]));
                }
            }
            // ── Réinjection dans les formulaires manuels ─────────────────────────
            setExternalComparables((prev) => {
                const isStillEmpty = prev.length === 1 &&
                    !prev[0].title &&
                    !prev[0].artist &&
                    !prev[0].price;
                if (!isStillEmpty || !rows.length)
                    return prev;
                const rehydrated = rows
                    .filter((r) => r.selected_price || r.title_snapshot || r.artist_snapshot)
                    .map((r) => {
                    const comp = comparablesByid[r.comparable_id] || {};
                    return {
                        ...emptyComparableRow(),
                        title: r.title_snapshot || '',
                        artist: r.artist_snapshot || '',
                        source: r.source_snapshot || comp.source || '',
                        price: r.selected_price ? String(r.selected_price) : '',
                        // auction_date depuis estimation_comparables.
                        // Si null (ligne créée manuellement sans date saisie), laisser vide
                        // pour que l'expert puisse la compléter — ne jamais mettre created_at.
                        date: comp.auction_date
                            ? comp.auction_date // format ISO YYYY-MM-DD, le champ input[type=date] l'accepte
                            : '',
                        // source_file = lien Artprice uniquement si URL http(s) — sinon UUID de blob PDF
                        url: (comp.source_file || '').startsWith('http') ? comp.source_file : '',
                        _selectedId: r.id,
                        comparable_id: r.comparable_id,
                        weight: r.weight ?? 1,
                        comment: r.note || '',
                    };
                });
                return rehydrated.length ? rehydrated : [emptyComparableRow()];
            });
        }
        loadSelectedDecisions();
        return () => {
            cancelled = true;
        };
    }, [resolvedEstimationId, currentUserId]);
    // ── Chargement des autres œuvres du lot (pour application en masse) ─────
    useEffect(() => {
        let cancelled = false;
        async function loadLotMembers() {
            if (!artwork?.lot_id || !artwork?.id) {
                if (!cancelled) {
                    setLotMembers([]);
                    setLotInfo(null);
                }
                return;
            }
            setLotMembersLoading(true);
            try {
                // Champs nécessaires au recalcul de la décote individuelle :
                // - condition : décote état (getConditionDiscount)
                // - certificate_auth, certificate_authenticity : bonus certificat +5%
                // - provenance, acquisition_source : bonus provenance artiste +2%
                // - acquisition_date : bonus suivi documentaire +5% (>20 ans)
                //
                // IMPORTANT : ces colonnes doivent exister dans v_expert_artworks.
                // Ne pas demander has_certificate / has_provenance_direct / has_documentary_tracking
                // qui n'existent pas dans la vue et font planter TOUTE la requête.
                const SELECT_COLS = 'id, title, inventory_number, condition, lot_id, client_id, collection_id, certificate_auth, certificate_authenticity, provenance, acquisition_source, acquisition_date';
                const [ownRes, sharedRes] = await Promise.all([
                    supabase.from('artworks').select(SELECT_COLS).eq('lot_id', artwork.lot_id).neq('id', artwork.id),
                    supabase.from('v_expert_artworks').select(SELECT_COLS).eq('lot_id', artwork.lot_id).neq('id', artwork.id),
                ]);
                if (cancelled)
                    return;
                const ownData = ownRes.data || [];
                const sharedData = sharedRes.data || [];
                const merged = [...ownData, ...sharedData];
                const unique = merged.filter((a, i, arr) => arr.findIndex((b) => b.id === a.id) === i);
                setLotMembers(unique);
                // Log diagnostique systématique — pour comprendre où le RLS bloque
                console.log('[LotMembers] diagnostic', {
                    lot_id: artwork.lot_id,
                    artwork_id: artwork.id,
                    source_artworks_table: {
                        count: ownData.length,
                        error: ownRes.error,
                        sample: ownData.slice(0, 3).map(a => ({ id: a.id, inv: a.inventory_number, client_id: a.client_id })),
                    },
                    source_v_expert_artworks: {
                        count: sharedData.length,
                        error: sharedRes.error,
                        sample: sharedData.slice(0, 3).map(a => ({ id: a.id, inv: a.inventory_number, client_id: a.client_id })),
                    },
                    merged_unique_count: unique.length,
                    artwork_lot_joined: artwork.lot,
                });
                if (ownData.length === 0 && sharedData.length === 0) {
                    console.warn('[LotMembers] AUCUNE œuvre trouvée. Probable cause RLS sur artworks ou vue v_expert_artworks qui filtre trop.');
                }
                // Charger les infos du lot (titre, nombre d'exemplaires)
                // Idem : on tente la table directe, et si vide on essaie un fallback
                try {
                    const { data: lotData } = await supabase
                        .from('lots')
                        .select('id, title, total_copies, edition_info, pricing_mode')
                        .eq('id', artwork.lot_id)
                        .maybeSingle();
                    if (!cancelled && lotData) {
                        setLotInfo(lotData);
                    }
                    else if (!cancelled && artwork?.lot) {
                        // Fallback : si l'artwork a été chargé avec sa jointure lot (improbable
                        // ici car select('*') mais on prend la sécurité)
                        setLotInfo(artwork.lot);
                    }
                }
                catch (lotErr) {
                    // RLS peut bloquer l'expert sur la table lots — non bloquant
                    console.warn('[LotInfo] requête bloquée :', lotErr);
                }
            }
            catch (err) {
                console.warn('Chargement membres du lot :', err);
                if (!cancelled)
                    setLotMembers([]);
            }
            finally {
                if (!cancelled)
                    setLotMembersLoading(false);
            }
        }
        loadLotMembers();
        return () => { cancelled = true; };
    }, [artwork?.lot_id, artwork?.id, artwork?.lot]);
    useEffect(() => {
        let cancelled = false;
        async function loadPersonalArtistQuote() {
            if (!artwork?.artist || !currentUserId || !['expert', 'admin', 'team'].includes(userRole)) {
                setArtistQuote(null);
                setArtistQuoteOptions([]);
                setSelectedArtistQuoteId('');
                setQuoteSuggestion(null);
                return;
            }
            setArtistQuoteLoading(true);
            setArtistQuoteError(null);
            try {
                const artistKey = normalizeKey(artwork.artist);
                const artistKeys = artistKeyVariants(artwork.artist);
                const mediumKey = normalizeKeyUnderscore(artwork.medium_display_override ||
                    artwork.medium_category ||
                    artwork.medium_family_key ||
                    artwork.medium ||
                    artwork.typeOfArt ||
                    '');
                let { data, error } = await supabase
                    .from('artist_quotes')
                    .select('*')
                    .eq('expert_id', currentUserId)
                    .in('artist_key', artistKeys)
                    .order('updated_at', { ascending: false })
                    .limit(20);
                if (error)
                    throw error;
                if (!data?.length) {
                    // Fallback robuste : certains anciens artist_key sont stockés avec espaces
                    // et certains noms diffèrent par accents (BELZÈRE/BELZERE). On récupère
                    // les cotations de l'expert puis on filtre côté client avec normalizeKey().
                    const fallback = await supabase
                        .from('artist_quotes')
                        .select('*')
                        .eq('expert_id', currentUserId)
                        .order('updated_at', { ascending: false })
                        .limit(300);
                    if (fallback.error)
                        throw fallback.error;
                    data = (fallback.data || []).filter((quote) => {
                        const qArtistKey = normalizeKey(quote.artist_key || quote.artist || '');
                        return qArtistKey === artistKey;
                    }).slice(0, 20);
                }
                // Sélection de la cotation la plus pertinente parmi celles de l'artiste
                const artworkH = getArtworkHeight(artwork);
                const artworkW = getArtworkWidth(artwork);
                const artworkDim = artworkH && artworkW ? artworkH + artworkW : null;
                // Famille de médium de l'œuvre (peinture / dessin / photo / sculpture / estampe)
                const mediumFamilyOf = (m) => {
                    const s = normalizeKey(m || '');
                    if (/peinture|huile|toile|acrylic|oil|canvas|acrylique/.test(s))
                        return 'peinture';
                    if (/dessin|aquarelle|gouache|encre|papier|lavis|crayon/.test(s))
                        return 'dessin';
                    if (/photo|tirage|argentique|lambda|pigment|numerique/.test(s))
                        return 'photo';
                    if (/sculpture|bronze|marbre|ceramique|installation/.test(s))
                        return 'sculpture';
                    if (/estampe|serigraphie|lithographie|gravure|print/.test(s))
                        return 'estampe';
                    return 'autre';
                };
                const artworkFamily = mediumFamilyOf(artwork.medium || artwork.typeOfArt);
                const scoreQuote = (q) => {
                    let score = 0;
                    const qFamily = mediumFamilyOf(q.medium || '');
                    // Famille médium identique : fort bonus
                    if (qFamily === artworkFamily)
                        score += 100;
                    // Médium key match partiel
                    if (mediumKey && normalizeKeyUnderscore(q.medium || '').includes(mediumKey))
                        score += 50;
                    // Proximité dimensionnelle (si l'œuvre a des dimensions)
                    if (artworkDim && q.reference_h_cm && q.reference_w_cm) {
                        const qDim = Number(q.reference_h_cm) + Number(q.reference_w_cm);
                        const diff = Math.abs(artworkDim - qDim) / Math.max(artworkDim, qDim);
                        score += Math.round((1 - diff) * 40); // max 40 pts pour proximité dim
                    }
                    return score;
                };
                const sorted = [...(data || [])].sort((a, b) => scoreQuote(b) - scoreQuote(a));
                const quote = sorted[0] || null;
                if (!cancelled) {
                    setArtistQuoteOptions(sorted);
                    setSelectedArtistQuoteId(quote?.id || '');
                    setArtistQuote(quote);
                    setQuoteSuggestion(buildPointMethodSuggestion(artwork, quote));
                    // Si une cotation personnelle exploitable existe, elle devient le mode par défaut.
                    // L'AutoCUP reste disponible via le switch, mais ne doit pas écraser le segment expert.
                    if (quote && !isGroupCMethodGroup(quote.method_group)) {
                        setValuationMode('personal');
                    }
                }
            }
            catch (error) {
                console.error('Erreur chargement artist_quotes:', error);
                if (!cancelled) {
                    setArtistQuote(null);
                    setArtistQuoteOptions([]);
                    setSelectedArtistQuoteId('');
                    setArtistQuoteError(error.message || 'Impossible de vérifier la cotation personnelle artiste.');
                }
            }
            finally {
                if (!cancelled)
                    setArtistQuoteLoading(false);
            }
        }
        loadPersonalArtistQuote();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artwork, currentUserId, userRole]);
    const loadData = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session)
                return;
            setCurrentUserId(session.user.id);
            // Profil utilisateur
            const { data: profile } = await supabase
                .from('profiles')
                .select('plan, role')
                .eq('id', session.user.id)
                .single();
            setUserRole(profile?.role || 'user');
            setSubscriptionLevel(profile?.plan || 'standard');
            // Charger l'œuvre présélectionnée
            if (artworkId) {
                // 1. Tentative propriétaire
                let artworkData = null;
                const { data: ownerArtwork } = await supabase
                    .from('artworks')
                    .select('*, lot:lot_id(id, title, total_copies, edition_info, pricing_mode)')
                    .eq('id', artworkId)
                    .single();
                if (ownerArtwork) {
                    artworkData = ownerArtwork;
                    setIsSharedArtwork(false);
                }
                else {
                    // 2. Fallback expert / accès partagé
                    const { data: sharedArtwork } = await supabase
                        .from('v_expert_artworks')
                        .select('*, lot:lot_id(id, title, total_copies, edition_info, pricing_mode)')
                        .eq('id', artworkId)
                        .single();
                    if (sharedArtwork) {
                        artworkData = sharedArtwork;
                        setIsSharedArtwork(true); // expert sur oeuvre partagee -> update via RPC
                    }
                }
                if (artworkData) {
                    setArtwork(artworkData);
                    if (artworkData.expert_note) {
                        // Nettoyer l'ancien texte automatique "Estimation appliquée depuis l'œuvre source (id:...)"
                        // qui polluait les notes des membres de lot et ne doit pas être ré-enregistré
                        const cleanedNote = artworkData.expert_note
                            .replace(/\s*·?\s*Estimation appliqu[eé]e? depuis l[''].*?\(id:[^)]+\)/gi, '')
                            .trim();
                        setExpertFreeNote(cleanedNote);
                    }
                }
            }
            else if (location.state?.artworkId) {
                // Fallback depuis navigation
                const fallbackArtworkId = location.state.artworkId;
                let artworkData = null;
                const { data: ownerArtwork } = await supabase
                    .from('artworks')
                    .select('*, lot:lot_id(id, title, total_copies, edition_info, pricing_mode)')
                    .eq('id', fallbackArtworkId)
                    .single();
                if (ownerArtwork) {
                    artworkData = ownerArtwork;
                    setIsSharedArtwork(false);
                }
                else {
                    const { data: sharedArtwork } = await supabase
                        .from('v_expert_artworks')
                        .select('*, lot:lot_id(id, title, total_copies, edition_info, pricing_mode)')
                        .eq('id', fallbackArtworkId)
                        .single();
                    if (sharedArtwork) {
                        artworkData = sharedArtwork;
                        setIsSharedArtwork(true); // expert sur oeuvre partagee -> update via RPC
                    }
                }
                if (artworkData) {
                    setArtwork(artworkData);
                    if (artworkData.expert_note) {
                        // Nettoyer l'ancien texte automatique "Estimation appliquée depuis l'œuvre source (id:...)"
                        // qui polluait les notes des membres de lot et ne doit pas être ré-enregistré
                        const cleanedNote = artworkData.expert_note
                            .replace(/\s*·?\s*Estimation appliqu[eé]e? depuis l[''].*?\(id:[^)]+\)/gi, '')
                            .trim();
                        setExpertFreeNote(cleanedNote);
                    }
                    navigate(`/estimation-semblables/${fallbackArtworkId}`, { replace: true });
                }
            }
        }
        catch (error) {
            console.error('Erreur chargement:', error);
        }
    };
    const addComparable = () => {
        if (externalComparables.length >= MAX_MANUAL_COMPARABLES) {
            alert(i18next.t("estimationSemblables.dynamic.maximumComparables", { count: MAX_MANUAL_COMPARABLES }));
            return;
        }
        setExternalComparables((prev) => [...prev, emptyComparableRow()]);
    };
    const updateComparable = (index, field, value) => {
        setExternalComparables((prev) => {
            const updated = [...prev];
            updated[index] = { ...(updated[index] || emptyComparableRow()), [field]: value };
            return updated;
        });
    };
    const removeComparable = (index) => {
        setExternalComparables((prev) => {
            const next = prev.filter((_, i) => i !== index);
            return next.length ? next : [emptyComparableRow()];
        });
    };
    const validComparables = externalComparables.filter((c) => c.title && c.artist && c.price);
    const minimumComparables = useMemo(() => {
        if (isGroupCMethodGroup(artistQuote?.method_group))
            return 0;
        if (artistQuote?.method_group === 'analogy_cup')
            return 5;
        return 3;
    }, [artistQuote]);
    const personalQuoteLow = toNumber(artistQuote?.price_low);
    const personalQuoteHigh = toNumber(artistQuote?.price_high);
    // Valeur retenue expert pour une cotation personnelle.
    // Important : average_value est parfois une moyenne indicative de segment (ex. 2 750 €),
    // mais l'expert peut retenir une valeur de prudence bonus compris (ex. 2 500 €).
    // On lit d'abord les éventuels champs d'override, puis seulement average_value en fallback.
    const personalQuoteRetained = toNumber(artistQuote?.retained_value_eur ??
        artistQuote?.expert_retained_value_eur ??
        artistQuote?.retained_value ??
        artistQuote?.expert_retained_value ??
        artistQuote?.valuation_retained ??
        artistQuote?.recommended_value_eur ??
        artistQuote?.recommended_value ??
        artistQuote?.average_value);
    const personalConditionDiscount = useMemo(() => getConditionDiscount(artwork?.condition), [artwork?.condition]);
    const personalQuoteAdjusted = useMemo(() => applyFinalMethodologyAdjustments({
        low: personalQuoteLow,
        high: personalQuoteHigh,
        retained: personalQuoteRetained,
        artwork,
        artistQuote,
        methodGroup: artistQuote?.method_group,
        validComparables,
        includeCondition: true,
        includeDocumentaryBonuses: true,
        includePrudence: true,
        preserveRetained: artistQuote?.method_group === 'analogy_cup',
        // Groupe C : la cotation personnelle / méthode à points doit toujours intégrer
        // l'abattement de liquidité par défaut (30/45/55), sauf write-off géré à part.
        includeLiquidity: isGroupCMethodGroup(artistQuote?.method_group),
    }), [personalQuoteLow, personalQuoteHigh, personalQuoteRetained, artwork, artistQuote, validComparables]);
    const documentaryBonuses = useMemo(() => computeDocumentaryBonuses(artwork || {}), [artwork]);
    const hasReusableArtistQuote = Boolean(artistQuote &&
        (isGroupCMethodGroup(artistQuote.method_group) ||
            personalQuoteLow ||
            personalQuoteHigh ||
            personalQuoteRetained));
    const hasPersonalQuoteValue = Boolean(personalQuoteLow ||
        personalQuoteHigh ||
        personalQuoteRetained ||
        quoteSuggestion?.low ||
        quoteSuggestion?.high ||
        quoteSuggestion?.retained);
    // En mode 'personal' : la cotation personnelle suffit si elle existe.
    // En mode 'cup' : il faut des comparables ou une quote réutilisable.
    const canContinueToValidation = !quoteSuggestion?.isWriteOff && ((valuationMode === 'personal' && Boolean(artistQuote && hasPersonalQuoteValue)) ||
        (valuationMode === 'cup' && (validComparables.length >= minimumComparables ||
            hasReusableArtistQuote ||
            Boolean(quoteSuggestion))));
    // Le switch est disponible dès qu'une cotation personnelle exploitable existe
    // et que l'on n'est pas en Groupe C méthode à points.
    const canSwitchMode = Boolean(artistQuote && hasPersonalQuoteValue && !quoteSuggestion?.isWriteOff) &&
        !isGroupCMethodGroup(artistQuote?.method_group);
    const methodologySignal = useMemo(() => getMethodologySignal({
        artistQuote,
        comparables: autoCupRawComparables.length > 0 ? autoCupRawComparables : validComparables,
        confidence: quoteSuggestion?.confidence,
        t,
    }), [artistQuote, autoCupRawComparables, validComparables, quoteSuggestion, t]);
    const cupRawLow = quoteSuggestion?.rawLow ?? quoteSuggestion?.low ?? toNumber(validComparables[0]?.price) ?? null;
    const cupRawHigh = quoteSuggestion?.rawHigh ?? quoteSuggestion?.high ?? toNumber(validComparables[0]?.price) ?? cupRawLow ?? null;
    const cupRawRetained = (quoteSuggestion?.rawRetained ?? quoteSuggestion?.retained) ||
        (cupRawLow && cupRawHigh ? Math.round((cupRawLow + cupRawHigh) / 2) : cupRawLow || cupRawHigh || null);
    // Détecte si l'AutoCUP a traité des résultats propres récents (pour modulation prudence)
    // hasOwnRecentFromAutoCUP : détermine si la décote prudentielle Groupe B doit s'appliquer.
    // Règle méthodologique :
    //   0 résultat propre récent  → −15%
    //   résultats propres récents mais pas de segment primaire → −8%
    //   segment primaire + résultats propres récents → 0%
    //
    // Doit lire autoCupRawComparables (stock Artprice analysé par AutoCUP),
    // et NON validComparables (formulaires manuels, vides avant saisie).
    const allCupComparables = autoCupRawComparables.length > 0
        ? autoCupRawComparables
        : validComparables;
    // Priorité : autoCupMeta.hasOwnRecent transmis par AutoCUP (freshnessCoefficient > 0.25 ~5ans)
    // Les Enjoy 2022 ont freshness=0.35 → détectés comme "récents" par AutoCUP
    // mais exclus par isRecentComparable (< 3ans) → on lit autoCupMeta en priorité
    const hasOwnRecentFromAutoCUP = autoCupRawComparables.length > 0
        ? autoCupMeta.hasOwnRecent
        : Boolean(allCupComparables.some((c) => normalizeKey(c.artist || '') === normalizeKey(artwork?.artist || '') && isRecentComparable(c)));
    const cupFinalAdjusted = useMemo(() => {
        // Groupe C : buildPointMethodSuggestion renvoie déjà la valeur finale avec
        // état + abattement de liquidité + bonus. On réutilise ce calcul tel quel
        // pour éviter une double décote état ou, inversement, la perte de la décote
        // de liquidité lors d'une nouvelle expertise.
        if (isGroupCMethodGroup(artistQuote?.method_group) && quoteSuggestion?.finalAdjustments) {
            return quoteSuggestion.finalAdjustments;
        }
        return applyFinalMethodologyAdjustments({
            // Partir de la fourchette déjà pondérée par AutoCUP (après discountFactor par comparable),
            // et non des bornes brutes (cupRawLow/High). Les bruts restent affichés dans le récap
            // "Base brute (avant décotes)" mais ne doivent pas servir de base de calcul ici.
            low: quoteSuggestion?.low ?? cupRawLow,
            high: quoteSuggestion?.high ?? cupRawHigh,
            retained: quoteSuggestion?.retained ?? cupRawRetained,
            artwork,
            artistQuote,
            methodGroup: artistQuote?.method_group === 'analogy_cup' ? 'analogy_cup' : 'direct_cup',
            // allCupComparables : utilisé par getPrudenceDiscountPct pour détecter hasPrimarySegment
            // → détermine si la décote −15% / −8% / 0% s'applique
            validComparables: autoCupMeta.hasPrimarySegment
                ? [...allCupComparables, { marketSegment: 'primary', artist: artwork?.artist, price: 1 }]
                : allCupComparables,
            includeCondition: true,
            includeDocumentaryBonuses: true,
            includePrudence: artistQuote?.method_group === 'analogy_cup',
            includeLiquidity: false,
            hasOwnRecentFromAutoCUP,
        });
    }, [quoteSuggestion, cupRawLow, cupRawHigh, cupRawRetained, artwork, artistQuote, allCupComparables, autoCupMeta, hasOwnRecentFromAutoCUP]);
    // ── Prévisualisation lot : recalcule pour chaque membre, regroupe par condition
    const lotPreview = useMemo(() => {
        if (!artwork?.lot_id || lotMembers.length === 0)
            return null;
        // Bases brutes (avant tout ajustement) selon le mode
        const isPersonal = valuationMode === 'personal';
        const baseLow = isPersonal ? personalQuoteLow : cupRawLow;
        const baseHigh = isPersonal ? personalQuoteHigh : cupRawHigh;
        const baseRetained = isPersonal ? personalQuoteRetained : cupRawRetained;
        const methodGroup = artistQuote?.method_group;
        if (baseLow === null || baseHigh === null)
            return null;
        // L'œuvre courante en tête
        const allMembers = [artwork, ...lotMembers];
        // Recalculer pour chaque œuvre avec son propre condition
        const rows = allMembers.map((m) => {
            const adj = applyFinalMethodologyAdjustments({
                low: baseLow,
                high: baseHigh,
                retained: baseRetained,
                artwork: m,
                artistQuote,
                methodGroup: methodGroup === 'analogy_cup' ? 'analogy_cup' : (methodGroup || 'direct_cup'),
                validComparables,
                includeCondition: true,
                includeDocumentaryBonuses: true,
                includePrudence: methodGroup === 'analogy_cup',
                includeLiquidity: false,
            });
            const condDisc = getConditionDiscount(m?.condition);
            return {
                id: m.id,
                title: m.title || '',
                inventory_number: m.inventory_number || '',
                condition: m.condition || null,
                conditionLabel: condDisc.label || i18next.t("estimationSemblables.dynamic.goodVeryGood"),
                conditionPct: condDisc.pct,
                low: adj.low,
                high: adj.high,
                retained: adj.retained,
                isCurrent: m.id === artwork.id,
            };
        });
        // Regroupement par condition
        const byCondition = {};
        rows.forEach((r) => {
            const key = r.conditionLabel;
            if (!byCondition[key]) {
                byCondition[key] = { label: key, pct: r.conditionPct, count: 0, retained: r.retained };
            }
            byCondition[key].count += 1;
        });
        const groups = Object.values(byCondition).sort((a, b) => a.pct - b.pct);
        // Total retenu (somme des valeurs retenues individuelles)
        const totalRetained = rows.reduce((s, r) => s + (r.retained || 0), 0);
        const totalLow = rows.reduce((s, r) => s + (r.low || 0), 0);
        const totalHigh = rows.reduce((s, r) => s + (r.high || 0), 0);
        return {
            rows,
            groups,
            totalCount: rows.length,
            totalLow,
            totalHigh,
            totalRetained,
            baseLow,
            baseHigh,
            baseRetained,
            mode: valuationMode,
        };
    }, [
        artwork,
        lotMembers,
        valuationMode,
        personalQuoteLow,
        personalQuoteHigh,
        personalQuoteRetained,
        cupRawLow,
        cupRawHigh,
        cupRawRetained,
        artistQuote,
        validComparables,
    ]);
    // ── Construit le payload UPDATE pour une œuvre du lot
    // Stratégie : repartir des valeurs FINALES ajustées de l'œuvre source (après tous
    // les ajustements : condition, bonifications documentaires, prudence) puis appliquer
    // SEULEMENT la décote état différentielle du membre cible par rapport à l'œuvre source.
    // Cela garantit que la valeur retenue de l'œuvre source est exactement respectée,
    // et que chaque membre du lot reçoit la bonne décote selon son propre état.
    const buildLotMemberUpdate = (targetArtwork) => {
        const isPersonal = valuationMode === 'personal';
        const methodGroup = artistQuote?.method_group;
        // ── Valeurs finales de l'œuvre source (ce que l'expert a validé) ──
        const sourceAdj = isPersonal ? personalQuoteAdjusted : cupFinalAdjusted;
        const sourceLow = sourceAdj?.low ?? null;
        const sourceHigh = sourceAdj?.high ?? null;
        const sourceRetained = sourceAdj?.retained ?? null;
        // ── Décote état de l'œuvre source (déjà intégrée dans sourceAdj) ──
        const sourceCondDisc = getConditionDiscount(artwork?.condition);
        const sourceCondFactor = (100 - (sourceCondDisc?.pct || 0)) / 100;
        // ── Décote état du membre cible ──
        const memberCondDisc = getConditionDiscount(targetArtwork?.condition);
        const memberCondFactor = (100 - (memberCondDisc?.pct || 0)) / 100;
        // ── Base normalisée : annuler la décote source, appliquer la décote membre ──
        // Si l'œuvre source n'a pas de décote (factor=1), la base = valeur finale.
        // Si elle en a une, on remonte à la base pré-décote puis on applique celle du membre.
        const applyDiff = (val) => {
            if (val === null || val === undefined)
                return null;
            if (sourceCondFactor === 0)
                return 0; // cas irréparable source
            const baseBeforeCondition = Math.round(val / sourceCondFactor);
            return Math.round(baseBeforeCondition * memberCondFactor);
        };
        const adjLow = applyDiff(sourceLow);
        const adjHigh = applyDiff(sourceHigh);
        const adjRetained = applyDiff(sourceRetained);
        const method = isPersonal
            ? `personal_quote_${methodGroup || 'direct'}`
            : isGroupCMethodGroup(methodGroup)
                ? 'point_method'
                : methodGroup === 'analogy_cup'
                    ? 'analogy_cup'
                    : 'comparables';
        const galleryCapNum = toNumber(galleryCap);
        const condLabel = memberCondDisc?.label
            ? `Décote état membre : ${memberCondDisc.label}`
            : null;
        const adjustmentNote = [condLabel].filter(Boolean).join(' · ') || null;
        // cup_comparables : on garde la même structure mais propre à chaque œuvre
        const cupComparables = {
            origin: 'estimation_semblables_lot',
            lot_id: targetArtwork.lot_id,
            lot_source_artwork_id: artwork.id,
            method_group: methodGroup || 'direct_cup',
            valuation_low: adjLow,
            valuation_high: adjHigh,
            valuation_retained: adjRetained,
            valuation_mode: valuationMode,
            gallery_price_cap: galleryCapNum,
            gallery_price_cap_note: galleryCapNum ? 'Borne haute indicative uniquement — non intégrée dans la moyenne pondérée' : null,
            // Comparables utilisés sur l'œuvre source (référence pour traçabilité)
            comparables_used: validComparables.map((c, i) => ({
                rank: i + 1,
                title: c.title || '',
                artist: c.artist || '',
                price: toNumber(c.price),
                date: c.date || null,
                source: c.source || '',
                url: c.url || '',
                market_segment: getComparableMarketSegment(c) || null,
                market_segment_label: c.marketSegmentLabel || c.market_segment_label || null,
                market_weight_multiplier: toNumber(c.marketWeightMultiplier ?? c.market_weight_multiplier),
                technique_coefficient: toNumber(c.techniqueCoefficient ?? c.technique_coefficient),
            })),
            adjustments: null,
            adjustment_note: adjustmentNote || null,
            saved_at: new Date().toISOString(),
        };
        const isExpertRole = ['expert', 'admin', 'team'].includes(userRole);
        return {
            valuation_low: adjLow,
            valuation_high: adjHigh,
            valuation_method: method,
            last_valuation_date: new Date().toISOString().slice(0, 10),
            last_estimation_date: new Date().toISOString().slice(0, 10),
            current_estimated_value: adjRetained,
            valuation_estimated_value: adjRetained,
            valuation_consensus: adjRetained,
            estimated_value: adjRetained,
            cup_comparables: cupComparables,
            ...(isExpertRole ? {
                expert_method: method,
                expert_date: new Date().toISOString().slice(0, 10),
                expert_note: buildAutoExpertNote({
                    expertFreeNote,
                    artistQuote,
                    quoteSuggestion,
                    adjustmentNote,
                    galleryCapNum,
                }),
                valuation_validated_by: currentUserId,
            } : {}),
        };
    };
    // ── Applique l'estimation à toutes les œuvres du lot (hors œuvre courante)
    // Chaque œuvre conserve sa décote état propre via son champ `condition`.
    const applyEstimationToLot = async () => {
        if (!artwork?.lot_id || lotMembers.length === 0)
            return { success: 0, errors: [] };
        setLotApplying(true);
        const result = { success: 0, errors: [] };
        try {
            for (const member of lotMembers) {
                const payload = buildLotMemberUpdate(member);
                // forceRpc=true : les membres du lot appartiennent au client (pas à l'expert)
                // → RLS bloque le UPDATE direct → on force la RPC SECURITY DEFINER
                const { error } = await updateArtworkSafe(member.id, payload, true);
                if (error) {
                    console.error('[applyEstimationToLot] Erreur membre', member.inventory_number, error);
                    result.errors.push({ id: member.id, inv: member.inventory_number, message: error.message });
                }
                else {
                    result.success += 1;
                }
            }
        }
        catch (err) {
            result.errors.push({ message: err.message || String(err) });
        }
        finally {
            setLotApplying(false);
            setLotApplyResult(result);
        }
        return result;
    };
    // ── Sauvegarde de la valuation sur l'artwork ────────────────────────────
    // Helper : route l'update artwork vers direct (propriétaire) ou RPC (expert/shared).
    // Raison : la RLS de artworks bloque les UPDATE directs pour les experts sur
    // des oeuvres dont ils ne sont pas client_id. La RPC update_artwork_as_collaborator
    // est SECURITY DEFINER et vérifie collection_permissions.can_edit en interne.
    const updateArtworkSafe = async (artworkId, payload, forceRpc = false) => {
        // Décision de routage :
        // - isSharedArtwork=true  : artwork chargé depuis v_expert_artworks → RPC obligatoire
        // - forceRpc=true         : membres du lot (client_id ≠ expert) → RPC obligatoire
        // - artwork.client_id ≠ currentUserId : l'artwork courant appartient au client,
        //   RLS bloque l'UPDATE direct même si isSharedArtwork=false (cas où la RLS SELECT
        //   autorise la lecture via collection_permissions mais l'UPDATE est refusé).
        //   On détecte ce cas en comparant client_id de l'artwork courant à l'utilisateur connecté.
        const artworkOwnedByCurrentUser = artwork?.client_id === currentUserId;
        const useRpc = isSharedArtwork || forceRpc || !artworkOwnedByCurrentUser;
        if (useRpc) {
            // Chemin expert / collaborateur : RPC SECURITY DEFINER
            const { data, error } = await supabase.rpc('update_artwork_as_collaborator', {
                p_artwork_id: artworkId,
                p_payload: payload,
            });
            // La RPC retourne {success: false, error: '...'} en cas de refus — traiter comme erreur
            if (!error && data?.success === false) {
                console.error('[updateArtworkSafe] RPC refusée:', data.error, '| artworkId:', artworkId);
                return { error: new Error(data.error || 'RPC refusée') };
            }
            return { error };
        }
        else {
            // Chemin propriétaire : update direct (client_id = currentUserId)
            const { error } = await supabase
                .from('artworks')
                .update(payload)
                .eq('id', artworkId);
            return { error };
        }
    };
    const saveValuationToArtwork = async () => {
        if (!artwork?.id)
            return false;
        try {
            const selectedAdjustments = valuationMode === 'personal'
                ? personalQuoteAdjusted
                : cupFinalAdjusted;
            const low = selectedAdjustments.low;
            const high = selectedAdjustments.high;
            const method = valuationMode === 'personal'
                ? `personal_quote_${artistQuote?.method_group || 'direct'}`
                : isGroupCMethodGroup(artistQuote?.method_group)
                    ? 'point_method'
                    : artistQuote?.method_group === 'analogy_cup'
                        ? 'analogy_cup'
                        : 'comparables';
            const retained = selectedAdjustments.retained;
            const adjustmentNote = formatAdjustmentNotes(selectedAdjustments);
            // Construire cup_comparables enrichi avec borne galerie + liste comparables utilisés
            const galleryCapNum = toNumber(galleryCap);
            const cupComparables = {
                origin: 'estimation_semblables',
                method_group: artistQuote?.method_group || 'direct_cup',
                valuation_low: low,
                valuation_high: high,
                valuation_retained: retained,
                valuation_mode: valuationMode,
                gallery_price_cap: galleryCapNum,
                gallery_price_cap_note: galleryCapNum ? 'Borne haute indicative uniquement — non intégrée dans la moyenne pondérée' : null,
                // Comparables manuels saisis par l'expert (conservés pour traçabilité et envoi client)
                comparables_used: validComparables.map((c, i) => ({
                    rank: i + 1,
                    title: c.title || '',
                    artist: c.artist || '',
                    price: toNumber(c.price),
                    date: c.date || null,
                    source: c.source || '',
                    url: c.url || '',
                    market_segment: getComparableMarketSegment(c) || null,
                    market_segment_label: c.marketSegmentLabel || c.market_segment_label || null,
                    market_weight_multiplier: toNumber(c.marketWeightMultiplier ?? c.market_weight_multiplier),
                    technique_coefficient: toNumber(c.techniqueCoefficient ?? c.technique_coefficient),
                })),
                adjustments: selectedAdjustments?.details || null,
                adjustment_note: adjustmentNote || null,
                saved_at: new Date().toISOString(),
            };
            const isExpertRole = ['expert', 'admin', 'team'].includes(userRole);
            const valuationPayload = {
                valuation_low: low,
                valuation_high: high,
                valuation_method: method,
                last_valuation_date: new Date().toISOString().slice(0, 10),
                last_estimation_date: new Date().toISOString().slice(0, 10),
                current_estimated_value: retained,
                valuation_estimated_value: retained,
                valuation_consensus: retained,
                estimated_value: retained,
                cup_comparables: cupComparables,
                ...(isExpertRole ? {
                    expert_method: method,
                    expert_date: new Date().toISOString().slice(0, 10),
                    expert_note: buildAutoExpertNote({
                        expertFreeNote,
                        artistQuote,
                        quoteSuggestion,
                        adjustmentNote,
                        galleryCapNum,
                    }),
                    valuation_validated_by: currentUserId,
                } : {}),
            };
            const { error } = await runWithAbortRetry(() => updateArtworkSafe(artwork.id, valuationPayload), { retries: 2, label: i18next.t("estimationSemblables.ui.sauvegardeValuationArtwork") });
            if (error)
                throw error;
            return true;
        }
        catch (err) {
            if (isAbortLikeError(err)) {
                console.warn('Sauvegarde valuation interrompue après retry.', err?.message || err);
            }
            else {
                console.error('Erreur sauvegarde valuation:', err);
            }
            return false;
        }
    };
    // ── Mémoire expert des comparables retenus ──────────────────────────────
    // Règle saine :
    // - estimation_comparables = stock auction original, jamais une copie liée à l'œuvre estimée
    // - estimation_selected_comparables = lien estimation ↔ auction choisie par l'expert
    //
    // Donc :
    // - si AutoCUP fournit déjà un id de comparable, on n'insère QUE dans selected ;
    // - si l'expert saisit une vente vraiment manuelle sans id, on crée une ligne stock neutre
    //   dans estimation_comparables avec artwork_id = NULL, puis on la lie dans selected.
    const saveManualComparablesToDB = async () => {
        if (!validComparables.length || !artwork?.id || !currentUserId || !resolvedEstimationId)
            return;
        try {
            const ownArtistKey = normalizeKeyUnderscore(artwork.artist || '');
            const buildSelectedPayload = (comp, comparableId) => {
                const compArtistKey = normalizeKeyUnderscore(comp.artist || comp.artist_snapshot || '');
                const selectedPrice = getComparableSelectedPrice(comp) || 0;
                return {
                    estimation_id: resolvedEstimationId,
                    comparable_id: comparableId,
                    kind: compArtistKey && compArtistKey !== ownArtistKey ? 'analogy' : 'direct_sale',
                    selected_price: selectedPrice,
                    weight: toNumber(comp.effectiveWeight ?? comp.weight) || 1,
                    note: comp.comment || comp.note || comp.decision || comp.summary || null,
                    created_by: currentUserId,
                    artist_snapshot: comp.artist || comp.artist_name || comp.artist_snapshot || null,
                    title_snapshot: comp.title || comp.title_snapshot || null,
                    source_snapshot: comp.source || comp.auction_house || comp.source_snapshot || null,
                };
            };
            const selectedPayload = [];
            const manualRowsToCreate = [];
            validComparables.forEach((comp) => {
                const existingComparableId = getExistingComparableId(comp);
                if (existingComparableId) {
                    // Ligne déjà présente dans le stock : surtout ne pas la recopier dans estimation_comparables.
                    selectedPayload.push(buildSelectedPayload(comp, existingComparableId));
                    return;
                }
                const price = getComparableSelectedPrice(comp);
                const auctionDate = normalizeComparableDateForDB(comp.date || comp.auction_date || comp.sale_date);
                manualRowsToCreate.push({
                    // IMPORTANT : pas de artwork_id ici. Cette table stocke la vente source,
                    // le rattachement à l'œuvre estimée se fait via estimation_selected_comparables.
                    artwork_id: null,
                    estimation_id: null,
                    artist: comp.artist || '',
                    artist_key: normalizeKeyUnderscore(comp.artist || ''),
                    title: comp.title || '',
                    hammer_price: price || null,
                    estimated_value: price || 0,
                    auction_date: auctionDate,
                    source: comp.source || '',
                    auction_house: comp.auction_house || comp.source || null,
                    source_type: 'externe',
                    role_uploaded: 'expert',
                    created_by: currentUserId,
                    uploaded_by: currentUserId,
                    expert_id: currentUserId,
                    client_id: null,
                    medium: comp.medium || null,
                    material: comp.material || null,
                    dimensions: comp.dimensions || null,
                    height_cm: toNumber(comp.height_cm),
                    width_cm: toNumber(comp.width_cm),
                    depth_cm: toNumber(comp.depth_cm),
                    price_currency: comp.price_currency || 'EUR',
                    source_file: comp.url || comp.source_file || null,
                    comment: comp.comment || comp.note || null,
                });
            });
            if (manualRowsToCreate.length) {
                const { data: insertedRows, error: insertError } = await runWithAbortRetry(() => supabase
                    .from('estimation_comparables')
                    .insert(manualRowsToCreate)
                    .select('id, artist, title, source, auction_house, hammer_price, artist_key'), { retries: 1, label: i18next.t("estimationSemblables.ui.creationComparablesManuelsNeutres") });
                if (insertError) {
                    console.warn('Création comparables manuels neutres :', insertError.message);
                }
                else {
                    (insertedRows || []).forEach((row) => {
                        selectedPayload.push(buildSelectedPayload(row, row.id));
                    });
                }
            }
            if (!selectedPayload.length)
                return;
            const { data: selectedRows, error: selectedError } = await runWithAbortRetry(() => supabase
                .from('estimation_selected_comparables')
                .upsert(selectedPayload, {
                onConflict: 'estimation_id,comparable_id',
                ignoreDuplicates: false,
            })
                .select('*'), { retries: 1, label: i18next.t("estimationSemblables.ui.sauvegardeComparablesRetenus") });
            if (selectedError) {
                console.warn('Mémoire selected comparables :', selectedError.message);
                return;
            }
            if (selectedRows?.length) {
                setSelectedDecisions((prev) => {
                    const byComparable = new Map();
                    [...selectedRows, ...prev].forEach((row) => {
                        const key = row.comparable_id || row.id || `${row.artist_snapshot || ''}|${row.title_snapshot || ''}|${row.selected_price || ''}`;
                        const existing = byComparable.get(key);
                        if (!existing || comparableCompletenessScore(row) > comparableCompletenessScore(existing)) {
                            byComparable.set(key, row);
                        }
                    });
                    return Array.from(byComparable.values());
                });
            }
        }
        catch (error) {
            if (isAbortLikeError(error)) {
                console.warn('Mémoire selected comparables non bloquante : requête annulée par le navigateur.', error?.message || error);
                return;
            }
            console.warn('Mémoire selected comparables non bloquante :', error?.message || error);
            return;
        }
    };
    // ── Write-off : enregistrement oeuvre irréparable ─────────────────────────
    const saveWriteOff = async () => {
        if (!artwork?.id)
            return false;
        const today = new Date().toISOString().slice(0, 10);
        const noteText = writeOffNote.trim() || i18next.t("estimationSemblables.dynamic.defaultWriteOffNote");
        const isExpertRole = ['expert', 'admin', 'team'].includes(userRole);
        try {
            setWriteOffSaving(true);
            const { error } = await updateArtworkSafe(artwork.id, {
                valuation_low: 0,
                valuation_high: 0,
                valuation_method: 'write-off',
                last_valuation_date: today,
                last_estimation_date: today,
                current_estimated_value: 0,
                valuation_estimated_value: 0,
                valuation_consensus: 0,
                estimated_value: 0,
                cup_comparables: {
                    origin: 'estimation_semblables',
                    method_group: 'write-off',
                    valuation_low: 0,
                    valuation_high: 0,
                    valuation_retained: 0,
                    valuation_mode: valuationMode,
                    saved_at: new Date().toISOString(),
                },
                ...(isExpertRole ? {
                    expert_method: 'write-off',
                    expert_date: today,
                    expert_note: noteText,
                    valuation_validated_by: currentUserId,
                } : {}),
            });
            if (error)
                throw error;
            return true;
        }
        catch (err) {
            console.error('Erreur saveWriteOff:', err);
            return false;
        }
        finally {
            setWriteOffSaving(false);
        }
    };
    const handleWriteOffSubmit = async () => {
        if (!writeOffNote.trim())
            return; // note obligatoire
        const ok = await saveWriteOff();
        if (ok) {
            const artworkRoute = ['expert', 'admin', 'team'].includes(userRole)
                ? `/expert/artwork/${artwork.id}`
                : `/artwork/${artwork.id}`;
            navigate(artworkRoute, {
                state: { flash: i18next.t("estimationSemblables.dynamic.writeOffSaved") }
            });
        }
    };
    const handleSubmit = async () => {
        if (quoteSuggestion?.isWriteOff || (valuationMode === 'personal' && personalConditionDiscount?.pct === 100)) {
            setWriteOffMode(true);
            setTimeout(() => {
                document.getElementById('write-off-panel')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 80);
            return;
        }
        if (!canContinueToValidation) {
            alert(artistQuote
                ? t('estimationSemblables.save.missingQuote')
                : t('estimationSemblables.save.catalogFirst'));
            return;
        }
        const isExpert = ['expert', 'admin', 'team'].includes(userRole);
        if (isExpert) {
            // Expert : sauvegarde directe + retour fiche oeuvre
            const ok = await saveValuationToArtwork();
            if (ok) {
                await saveManualComparablesToDB();
                // Expert : /expert/artwork/:id, collectionneur : /artwork/:id
                const artworkRoute = ['expert', 'admin', 'team'].includes(userRole)
                    ? `/expert/artwork/${artwork.id}`
                    : `/artwork/${artwork.id}`;
                navigate(artworkRoute, {
                    state: { flash: i18next.t("estimationSemblables.dynamic.estimationSaved") }
                });
            }
            else {
                alert(t('estimationSemblables.save.valuationError'));
            }
        }
        else {
            // Collectionneur : sauvegarde valeur indicative + page demande-validation
            await saveValuationToArtwork();
            await saveManualComparablesToDB();
            navigate(`/estimation/demande-validation/${artwork.id}`, {
                state: {
                    estimationData: {
                        method: artistQuote?.method_group || 'comparables',
                        comparables: validComparables,
                        artworkId: artwork.id,
                        artistQuote: artistQuote || null,
                        quoteSuggestion: quoteSuggestion || null,
                        visionResult: visionResult || null,
                    }
                }
            });
        }
    };
    const runVisionComparableAnalysis = async () => {
        if (!artwork?.id)
            return;
        if (!canUseExpertVision) {
            alert(i18next.t("estimationSemblables.ui.cetteFonctionnaliteEstReserveeAuxComptesArtAdvisorPremium"));
            return;
        }
        if (validComparables.length < 3) {
            alert(i18next.t("estimationSemblables.ui.ajoutezAuMoins3ComparablesCompletsAvantDeLancer"));
            return;
        }
        try {
            setVisionLoading(true);
            setVisionResult(null);
            const payload = {
                artwork_id: artwork.id,
                title: artwork.title || '',
                artist: artwork.artist || '',
                medium: artwork.medium || '',
                year: artwork.year || '',
                dimensions: artwork.specifications?.dimensions || null,
                comparables: validComparables.map((c) => ({
                    title: c.title,
                    artist: c.artist,
                    source: c.source,
                    price: Number(c.price),
                    date: c.date,
                    url: c.url
                }))
            };
            const response = await fetch('/api/valuation/vision-comparables', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                throw new Error(`Erreur Vision (${response.status})`);
            }
            const data = await response.json();
            setVisionResult(data);
        }
        catch (error) {
            console.error('Erreur analyse Vision comparables:', error);
            alert(i18next.t("estimationSemblables.ui.impossibleDeLancerLAnalyseVisionPourLeMoment"));
        }
        finally {
            setVisionLoading(false);
        }
    };
    if (!artwork) {
        return (<div className="max-w-5xl mx-auto px-6 py-12 text-center">
        <h2 className="text-2xl font-serif mb-4">{i18next.t("estimationSemblables.dynamic.loading")}</h2>
        <p className="text-gray-600"><Trans i18nKey="estimationSemblables.ui.veuillezDAbordSelectionnerUneUvreDepuisLa"/>{' '}
          <button onClick={() => navigate('/estimation')} className="text-blue-600 underline"><Trans i18nKey="estimationSemblables.ui.pageDEstimation"/> </button>
        </p>
      </div>);
    }
    const artworkImage = artwork.images?.[0]?.url ||
        artwork.images?.[0]?.path ||
        artwork.image_url ||
        null;
    const artworkDimensions = artwork.specifications?.dimensions
        ? `${artwork.specifications.dimensions.height || '?'} × ${artwork.specifications.dimensions.width || '?'} ${artwork.specifications.dimensions.unit || 'cm'}`
        : artwork.dimensions || null;
    return (<div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <button onClick={() => navigate('/estimation')} className="flex items-center gap-2 text-gray-600 hover:text-black mb-4">
            <span>←</span><Trans i18nKey="estimationSemblables.ui.retourAuxEstimations"/> </button>
          <h1 className="text-4xl font-serif mb-2"><Trans i18nKey="estimationSemblables.ui.estimationParUvresComparables"/></h1>
          <p className="text-gray-600"><Trans i18nKey="estimationSemblables.ui.comparezVotreUvreAvecDesVentesRecentesSimilaires"/> </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Info pédagogique */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
          <h3 className="font-semibold text-blue-900 mb-2"><Trans i18nKey="estimationSemblables.ui.commentCaMarche"/> </h3>
          <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
            <li><Trans i18nKey="estimationSemblables.ui.recherchezDes"/> <strong><Trans i18nKey="estimationSemblables.ui.uvresSimilaires"/></strong><Trans i18nKey="estimationSemblables.ui.venduesRecemmentMemeArtisteStylePeriodeDimensions"/> </li>
            <li>
              Ajoutez <strong><Trans i18nKey="estimationSemblables.ui.auMoins3Comparables"/></strong><Trans i18nKey="estimationSemblables.ui.avecLeurPrixDeVente"/> </li>
            <li><Trans i18nKey="estimationSemblables.ui.indiquezVos"/> <strong>sources</strong><Trans i18nKey="estimationSemblables.ui.artpriceChristieSSothebySGaleries"/> </li>
            <li><Trans i18nKey="estimationSemblables.ui.lEquipeArtkiv"/> <strong><Trans i18nKey="estimationSemblables.ui.valideEtAffine"/></strong><Trans i18nKey="estimationSemblables.ui.votreEstimationSous4872h"/> </li>
          </ol>
        </div>

        {/* Étape 1 : Œuvre sélectionnée */}
        <div className="bg-white border border-gray-200 rounded-lg p-8 mb-4">
          <h2 className="text-2xl font-serif mb-6"><Trans i18nKey="estimationSemblables.ui.votreUvre"/></h2>

          <div className="flex items-start gap-6">
            {artworkImage && (<div className="w-32 h-32 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                <img src={artworkImage} alt={artwork.title || i18next.t("estimationSemblables.ui.uvre")} className="w-full h-full object-cover"/>
              </div>)}

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h3 className="text-xl font-semibold">
                  {artwork.title || i18next.t("estimationSemblables.ui.sansTitre")}
                </h3>

                {/* Badge lot ▣ doré — visible immédiatement si l'œuvre est dans un lot */}
                {artwork?.lot_id && (() => {
            // Source des infos lot : priorité à la jointure FK (toujours dispo via RLS artwork),
            // fallback sur lotInfo (chargé séparément, peut être bloqué par RLS sur lots)
            const lot = artwork.lot || lotInfo || {};
            const totalCopies = lot.total_copies || (lotMembers.length > 0 ? lotMembers.length + 1 : null);
            return (<span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border" style={{ color: '#c9a961', borderColor: '#c9a961', borderRadius: 2, fontFamily: 'system-ui' }}>
                      ▣ {lot.title || i18next.t("estimationSemblables.dynamic.lot")}
                      {totalCopies && (<span className="text-gray-500 font-normal">
                          — {totalCopies} {i18next.t("estimationSemblables.dynamic.example")}
                        </span>)}
                    </span>);
        })()}
              </div>

              {artwork.artist && (<p className="text-gray-700 mb-1"><Trans i18nKey="estimationSemblables.ui.artiste"/> {artwork.artist}</p>)}

              {artwork.medium && (<p className="text-gray-600 text-sm mb-1"><Trans i18nKey="estimationSemblables.ui.technique"/> {humanizeMedium(artwork.medium)}
                </p>)}

              {artworkDimensions && (<p className="text-gray-600 text-sm"><Trans i18nKey="estimationSemblables.ui.dimensions"/> {artworkDimensions}
                </p>)}
            </div>
          </div>
        </div>

        {/* Bandeau lot — rappel proactif visible dès le début du parcours */}
        {artwork?.lot_id && (() => {
            const lot = artwork.lot || lotInfo || {};
            const totalCopies = lot.total_copies || (lotMembers.length > 0 ? lotMembers.length + 1 : null);
            const memberCount = lotMembers.length;
            return (<div className="mb-8 rounded-lg p-5 flex items-start gap-4" style={{ background: '#fffbf2', border: '1px solid #c9a961' }}>
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#c9a961', color: 'white', fontSize: 18 }}>
                ▣
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm mb-1" style={{ color: '#1A1A1A', fontFamily: 'system-ui' }}><Trans i18nKey="estimationSemblables.ui.cetteUvreFaitPartie"/> {totalCopies ? i18next.t("estimationSemblables.dynamic.lotOf", { count: totalCopies }) : i18next.t("estimationSemblables.ui.dUnLot")}
                </p>
                <p className="text-sm text-gray-700 leading-relaxed mb-2"><Trans i18nKey="estimationSemblables.ui.pasBesoinDEstimerChaqueExemplaireSeparementALa"/> <strong><Trans i18nKey="estimationSemblables.ui.appliquerCetteEstimationAToutLeLotEnUn"/></strong><Trans i18nKey="estimationSemblables.ui.laDecoteSelonLEtatDeConservationSeraAppliquee"/> </p>
                {memberCount > 0 ? (<button type="button" onClick={() => {
                        const submitBtn = document.getElementById('estimation-submit-actions');
                        if (submitBtn)
                            submitBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }} className="text-xs underline hover:no-underline" style={{ color: '#c9a961', fontFamily: 'system-ui' }}><Trans i18nKey="estimationSemblables.ui.allerAuBoutonDApplicationAuLot"/> </button>) : (<p className="text-xs italic" style={{ color: '#996d2a' }}>
                    {lotMembersLoading ? i18next.t("estimationSemblables.ui.chargementDesAutresExemplaires") : i18next.t("estimationSemblables.ui.aucunAutreExemplaireDetectePourCeLotVerifiezLes")}
                  </p>)}
              </div>
            </div>);
        })()}

        {/* Étape 2 : Recherche comparables */}
        <div className="bg-white border border-gray-200 rounded-lg p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-serif mb-2"><Trans i18nKey="estimationSemblables.ui.trouverDesUvresComparables"/></h2>
              <p className="text-gray-600 text-sm"><Trans i18nKey="estimationSemblables.ui.recherchezSurArtpriceMaisonsDeVentesGaleries"/> </p>
            </div>

            <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded">
              {isGroupCMethodGroup(artistQuote?.method_group)
            ? isTimeBasedMedium(artwork?.medium)
                ? i18next.t("estimationSemblables.ui.forfaitUvreTemporelle") : i18next.t("estimationSemblables.ui.groupeCMethodeAPoints")
            : `${validComparables.length}/${i18next.t("estimationSemblables.dynamic.minimum", { count: minimumComparables })}`}
            </span>
          </div>

          {/* Conseils de recherche */}
          <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
            <h4 className="font-semibold mb-2 text-sm"><Trans i18nKey="estimationSemblables.ui.ouChercher"/></h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <a href="https://www.artprice.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"><Trans i18nKey="estimationSemblables.ui.artpriceBaseDeDonneesMondiale"/> </a>
              <a href="https://www.christies.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"><Trans i18nKey="estimationSemblables.ui.christieSVentesAuxEncheres"/> </a>
              <a href="https://www.sothebys.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"><Trans i18nKey="estimationSemblables.ui.sothebySVentesAuxEncheres"/> </a>
              <a href="https://www.artsy.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"><Trans i18nKey="estimationSemblables.ui.artsyGaleries"/> </a>
            </div>
          </div>

          {['expert', 'admin', 'team'].includes(userRole) && (<div className="mb-6">
              {artistQuoteLoading && (<div className="border border-gray-200 bg-gray-50 rounded p-4 text-sm text-gray-600"><Trans i18nKey="estimationSemblables.ui.verificationDeVotreCotationPersonnelleArtiste"/> </div>)}

              {!artistQuoteLoading && artistQuoteError && (<div className="border border-red-200 bg-red-50 rounded p-4 text-sm text-red-800">
                  {artistQuoteError}
                </div>)}

              {!artistQuoteLoading && !artistQuote && !artistQuoteError && (<div className="border border-amber-200 bg-amber-50 rounded p-4 text-sm text-amber-900">
                  <p className="font-medium"><Trans i18nKey="estimationSemblables.ui.artisteNonEncoreCatalogueDansVosCotationsPersonnelles"/></p>
                  <p className="mt-1"><Trans i18nKey="estimationSemblables.ui.classezDAbordLArtisteDansVosCotationsExpert"/> </p>
                  <button type="button" onClick={() => navigate(`/expert/artist-quotes?artist=${encodeURIComponent(artwork.artist || '')}`)} className="mt-3 px-4 py-2 bg-black text-white rounded text-xs hover:bg-gray-800"><Trans i18nKey="estimationSemblables.ui.cataloguerCetArtiste"/> </button>
                </div>)}

              {!artistQuoteLoading && artistQuote && (<div className="border border-purple-200 bg-purple-50 rounded p-4 text-sm text-purple-950">
                  <p className="font-medium"><Trans i18nKey="estimationSemblables.ui.cotationPersonnelleDetectee"/> {getMethodGroupLabel(artistQuote.method_group)}
                  </p>
                  <p className="mt-1 text-xs text-purple-800">
                    {humanizeMedium(artistQuote.medium) || i18next.t("estimationSemblables.ui.mediumNonRenseigne")} · {formatEuro(artistQuote.price_low)} – {formatEuro(artistQuote.price_high)}
                    {artistQuote.quote_date ? i18next.t("estimationSemblables.dynamic.quoteDate", { date: String(artistQuote.quote_date).slice(0, 10) }) : ''}
                  </p>

                  {getMicroSegmentLabel(artistQuote) && (<p className="mt-1 text-xs text-purple-700"><Trans i18nKey="estimationSemblables.ui.segmentSpecifiqueDetecte"/> <strong>{getMicroSegmentLabel(artistQuote)}</strong>
                    </p>)}

                  {artistQuoteOptions.length > 1 && (<div className="mt-3">
                      <label className="block text-xs font-medium text-purple-900 mb-1"><Trans i18nKey="estimationSemblables.ui.choisirUneCotationSegmentExpert"/> </label>
                      <select value={selectedArtistQuoteId || artistQuote?.id || ''} onChange={(e) => {
                        const nextId = e.target.value;
                        const nextQuote = artistQuoteOptions.find((quote) => quote.id === nextId) || null;
                        setSelectedArtistQuoteId(nextId);
                        setArtistQuote(nextQuote);
                        setQuoteSuggestion(buildPointMethodSuggestion(artwork, nextQuote));
                        if (nextQuote && !isGroupCMethodGroup(nextQuote.method_group)) {
                            setValuationMode('personal');
                        }
                    }} className="w-full border border-purple-200 rounded px-3 py-2 text-sm bg-white">
                        {artistQuoteOptions.map((quote) => {
                        const microSegment = getMicroSegmentLabel(quote);
                        return (<option key={quote.id} value={quote.id}>
                              {humanizeMedium(quote.medium) || i18next.t("estimationSemblables.ui.mediumNonRenseigne")} — {formatEuro(quote.price_low)}<Trans i18nKey="estimationSemblables.ui.a"/> {formatEuro(quote.price_high)}
                              {microSegment ? i18next.t("estimationSemblables.dynamic.segment", { segment: microSegment }) : ''}
                            </option>);
                    })}
                      </select>
                    </div>)}

                  {!isGroupCMethodGroup(artistQuote.method_group) && hasPersonalQuoteValue && personalQuoteAdjusted?.notes?.length > 0 && (<div className="mt-2 text-xs text-red-700"><Trans i18nKey="estimationSemblables.ui.ajustementsAppliquesALaCotationPersonnelle"/> <strong>{personalQuoteAdjusted.notes.join(' · ')}</strong>
                      {' '}<Trans i18nKey="estimationSemblables.ui.base"/> {formatEuro(personalQuoteAdjusted.details.baseLow)} – {formatEuro(personalQuoteAdjusted.details.baseHigh)}
                      {' '}<Trans i18nKey="estimationSemblables.ui.final"/> <strong>{formatEuro(personalQuoteAdjusted.low)} – {formatEuro(personalQuoteAdjusted.high)}</strong>
                    </div>)}

                  {documentaryBonuses.bonuses.length > 0 && (<div className="mt-2 text-xs text-green-700"><Trans i18nKey="estimationSemblables.ui.bonusDocumentairesDetectes"/> <strong>{documentaryBonuses.bonuses.map((b) => `${b.label} +${b.pct}%`).join(' · ')}</strong>
                      {' '}<Trans i18nKey="estimationSemblables.ui.total"/> <strong>+{documentaryBonuses.totalPct}%</strong><Trans i18nKey="estimationSemblables.ui.integreALaMethodologieFinale"/> </div>)}

                  {quoteSuggestion && (<div className="mt-2 text-xs text-purple-800 space-y-0.5">
                      {quoteSuggestion.isWriteOff ? (<p className="text-red-800 font-medium"><Trans i18nKey="estimationSemblables.ui.irreparableDepreciationTotaleValeurNulle"/> {quoteSuggestion.baseLow ? i18next.t("estimationSemblables.dynamic.theoreticalBase", { low: formatEuro(quoteSuggestion.baseLow), high: formatEuro(quoteSuggestion.baseHigh) }) : ''}
                        </p>) : quoteSuggestion.isForfait ? (<p>
                          {quoteSuggestion.timeBased
                            ? <><span className="italic"><Trans i18nKey="estimationSemblables.ui.uvreTemporelleVideoInstallationFormulePointsInapplicable"/></span>{' '}<Trans i18nKey="estimationSemblables.ui.forfait"/></>
                            : <><Trans i18nKey="estimationSemblables.ui.forfaitCotationHorsFormulePoints"/></>}
                          {' '}<strong>{formatEuro(quoteSuggestion.low)} – {formatEuro(quoteSuggestion.high)}</strong>
                          {' '}<Trans i18nKey="estimationSemblables.ui.retenu"/> {formatEuro(quoteSuggestion.retained)}
                        </p>) : quoteSuggestion.hasDimensions ? (<p><Trans i18nKey="estimationSemblables.ui.formule"/>{quoteSuggestion.height} + {quoteSuggestion.width}<Trans i18nKey="estimationSemblables.ui.cm"/> {' '}<Trans i18nKey="estimationSemblables.ui.text"/> {quoteSuggestion.pointLow ?? '?'}–{quoteSuggestion.pointHigh ?? '?'}<Trans i18nKey="estimationSemblables.ui.pt"/> {' '}→ <strong>{formatEuro(quoteSuggestion.low)} – {formatEuro(quoteSuggestion.high)}</strong>
                          {' '}<Trans i18nKey="estimationSemblables.ui.retenu"/> {formatEuro(quoteSuggestion.retained)}
                        </p>) : (<p><Trans i18nKey="estimationSemblables.ui.forfaitExpert"/> <strong>{formatEuro(quoteSuggestion.low)} – {formatEuro(quoteSuggestion.high)}</strong>
                          {' '}<Trans i18nKey="estimationSemblables.ui.retenu"/> {formatEuro(quoteSuggestion.retained)}
                          <span className="ml-1 italic"><Trans i18nKey="estimationSemblables.ui.dimensionsAbsentesFormuleNonApplicable"/></span>
                        </p>)}
                      {quoteSuggestion.isOverride && (<p className="text-purple-500 italic"><Trans i18nKey="estimationSemblables.ui.overrideExpertActifPriceLowPriceHighExclusDu"/></p>)}
                      {quoteSuggestion.conditionDiscount?.pct > 0 && (<p className="mt-1 text-red-700"><Trans i18nKey="estimationSemblables.ui.decoteConservation"/> <strong>{quoteSuggestion.conditionDiscount.label}</strong>
                          {' '}<Trans i18nKey="estimationSemblables.ui.base"/> {formatEuro(quoteSuggestion.baseLow)} – {formatEuro(quoteSuggestion.baseHigh)}
                          {' '}<Trans i18nKey="estimationSemblables.ui.apresDecote"/> <strong>{formatEuro(quoteSuggestion.low)} – {formatEuro(quoteSuggestion.high)}</strong>
                        </p>)}
                      {(() => {
                        const { bonuses, totalPct } = computeDocumentaryBonuses(artwork);
                        if (!bonuses.length)
                            return null;
                        return (<p className="mt-1 text-green-700"><Trans i18nKey="estimationSemblables.ui.bonusDocumentairesAppliques"/> {bonuses.map((b) => `${b.label} +${b.pct}%`).join(' · ')}
                            {' '}→ <strong>+{totalPct}<Trans i18nKey="estimationSemblables.ui.integreDansLaValeurFinale"/></strong>
                          </p>);
                    })()}
                    </div>)}

                  {quoteSuggestion?.finalAdjustments && (<FinalMethodologyPanel title={i18next.t("estimationSemblables.ui.methodologieFinaleGroupeC")} adjustments={quoteSuggestion.finalAdjustments}/>)}
                </div>)}
            </div>)}

          {artistQuote && (<MethodologyReadingCard signal={methodologySignal} confidence={quoteSuggestion?.confidence}/>)}

          {/* ── Borne haute galerie — tous groupes ───────────────────────── */}
          {artistQuote && (<div className="border border-gray-200 bg-white rounded-lg p-4 mt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-gray-700 mb-1"><Trans i18nKey="estimationSemblables.ui.prixGalerieActuelArtsyArtsperGalerieBorneHauteIndicative"/> </label>
                  <p className="text-[11px] text-gray-400 mb-2"><Trans i18nKey="estimationSemblables.ui.jamaisIntegreDansLaMoyennePondereeSertDePlafond"/> </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input type="number" min="0" step="100" value={galleryCap} onChange={(e) => setGalleryCap(e.target.value)} className="border border-gray-300 rounded px-3 py-1.5 text-sm w-36" placeholder={i18next.t("estimationSemblables.ui.ex23000")}/>
                    <span className="text-sm text-gray-500">€</span>
                    {galleryCap && (quoteSuggestion?.high || personalQuoteAdjusted?.high) && (() => {
                const cap = toNumber(galleryCap);
                const computedHigh = quoteSuggestion?.high || personalQuoteAdjusted?.high;
                if (!cap || !computedHigh)
                    return null;
                return cap < computedHigh ? (<span className="text-[11px] px-2 py-1 rounded bg-amber-100 text-amber-800"><Trans i18nKey="estimationSemblables.ui.borneGalerie"/>{formatEuro(cap)}<Trans i18nKey="estimationSemblables.ui.inferieureALaFourchetteHauteCalculee"/>{formatEuro(computedHigh)}<Trans i18nKey="estimationSemblables.ui.coherenceAVerifier"/> </span>) : (<span className="text-[11px] px-2 py-1 rounded bg-gray-100 text-gray-600"><Trans i18nKey="estimationSemblables.ui.borneGalerie0f2755"/> {formatEuro(cap)}<Trans i18nKey="estimationSemblables.ui.auDessusDeLaFourchetteCalculee"/> </span>);
            })()}
                  </div>
                </div>
              </div>
            </div>)}
          {canSwitchMode && (<div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg mt-4">
              <div className="flex-1 text-sm text-gray-700">
                {valuationMode === 'cup'
                ? <><Trans i18nKey="estimationSemblables.ui.modeActuel"/> <strong><Trans i18nKey="estimationSemblables.ui.fourchetteCalculee"/></strong><Trans i18nKey="estimationSemblables.ui.viaComparablesEncheres"/></>
                : (<><Trans i18nKey="estimationSemblables.ui.modeActuel"/> <strong><Trans i18nKey="estimationSemblables.ui.cotationPersonnelle"/></strong><Trans i18nKey="estimationSemblables.ui.expert"/>{formatEuro(personalQuoteAdjusted.low)} – {formatEuro(personalQuoteAdjusted.high)})
                      {personalQuoteAdjusted?.notes?.length ? (<span className="ml-1 text-red-700"><Trans i18nKey="estimationSemblables.ui.ajustements"/> {personalQuoteAdjusted.notes.join(' · ')}
                        </span>) : null}
                    </>)}
              </div>
              <button onClick={() => setValuationMode(v => v === 'cup' ? 'personal' : 'cup')} className="px-4 py-2 text-xs font-medium border border-gray-800 rounded hover:bg-gray-800 hover:text-white transition whitespace-nowrap">
                {valuationMode === 'cup'
                ? i18next.t("estimationSemblables.ui.partirSurLaCotationPersonnelle") : i18next.t("estimationSemblables.ui.revenirAuxComparablesCalcules")}
              </button>
            </div>)}

          {valuationMode === 'personal' && canSwitchMode && (<FinalMethodologyPanel title={artistQuote?.method_group === 'analogy_cup'
                ? i18next.t("estimationSemblables.dynamic.finalPersonalGroupB", { suffix: (() => {
                    const pct = personalQuoteAdjusted?.details?.prudenceDiscountPct ?? 0;
                    if (pct === 0)
                        return i18next.t("estimationSemblables.ui.b3");
                    if (pct <= 8)
                        return i18next.t("estimationSemblables.ui.b1HybrideRenforce");
                    return i18next.t("estimationSemblables.ui.b2AnalogiePrudente");
                })() })
                : i18next.t("estimationSemblables.ui.methodologieFinaleCotationPersonnelleGroupeA")} adjustments={personalQuoteAdjusted}/>)}

        {/* AutoCUP / ratio marché : outils Art Advisor. Les montrer "verrouillés"
            à un collectionneur qui n'est même pas Art Advisor serait trompeur
            (rien ne les débloquerait pour lui) — réservés aux rôles expert. */}
        {!isGroupCMethodGroup(artistQuote?.method_group) && valuationMode === 'cup' && ['expert', 'admin', 'team'].includes(userRole) && (<>
    {estimationResolveError && (<div className="mb-4 border border-red-200 bg-red-50 text-red-700 rounded-lg p-3 text-xs">
        {estimationResolveError}
      </div>)}
    <AutoCUPAssistant artwork={artwork} estimationId={resolvedEstimationId} currentUserId={currentUserId} userRole={userRole} userPlan={subscriptionLevel} canUseAutoCUP={canUseAutoCUP} maxRows={MAX_MANUAL_COMPARABLES} artistQuote={artistQuote} onMethodologyComparablesChange={(rows, meta) => {
                setAutoCupRawComparables(rows || []);
                if (meta)
                    setAutoCupMeta({ hasOwnRecent: Boolean(meta.hasOwnRecentFromAutoCUP), hasPrimarySegment: Boolean(meta.hasPrimarySegmentFromAutoCUP) });
                // Propager la suggestion AutoCUP vers quoteSuggestion dès le chargement,
                // sans attendre que l'expert clique "Appliquer la suggestion".
                // Cela garantit que le score de confiance et la fourchette sont affichés
                // immédiatement dans ExpertMethodologyMemory et FinalMethodologyPanel.
                if (valuationMode !== 'personal' && meta && !meta.appendOnly && (meta.low || meta.retained || meta.high)) {
                    setQuoteSuggestion((prev) => {
                        // Ne pas écraser une suggestion que l'expert a déjà validée manuellement.
                        if (prev && prev._manuallyApplied)
                            return prev;
                        return {
                            low: meta.low,
                            high: meta.high,
                            retained: meta.retained,
                            median: meta.median,
                            rawLow: meta.rawLow,
                            rawHigh: meta.rawHigh,
                            rawRetained: meta.rawRetained,
                            soldCount: meta.soldCount,
                            confidence: meta.confidence,
                            canComputeFinal: meta.canComputeFinal,
                            canComputePreview: meta.canComputePreview,
                            status: meta.status,
                        };
                    });
                }
            }} onApplyPointSuggestion={(suggestion) => setQuoteSuggestion(suggestion)} onApplyComparables={(suggestedComparables, suggestion) => {
                if (suggestion && !suggestion.appendOnly)
                    setQuoteSuggestion({ ...suggestion, _manuallyApplied: true });
                if (!Array.isArray(suggestedComparables) || suggestedComparables.length === 0)
                    return;
                const sanitizedComparables = sanitizeComparableBatchForArtwork(suggestedComparables, artwork);
                // Ne jamais remplacer toute la sélection quand l'expert attrape UNE auction du stock.
                // Certains appels AutoCUP historiques n'envoyaient pas appendOnly, donc on sécurise aussi
                // par la taille du payload : 1 comparable = ajout, plusieurs comparables = suggestion complète.
                const hasExistingComparable = externalComparables.some((row) => row?.title || row?.artist || row?.price || row?.source || row?.date || row?.url);
                const shouldAppendComparable = Boolean(suggestion?.appendOnly) ||
                    (sanitizedComparables.length === 1 && hasExistingComparable);
                if (shouldAppendComparable) {
                    setExternalComparables((prev) => mergeComparableRows([...prev, ...sanitizedComparables], MAX_MANUAL_COMPARABLES));
                }
                else {
                    setExternalComparables(mergeComparableRows(sanitizedComparables, MAX_MANUAL_COMPARABLES));
                }
                if (resolvedEstimationId && currentUserId) {
                    const selectedPayload = suggestedComparables
                        .filter((c) => (c?.id || c?.source_id || c?.comparable_id) && (c?.hammer_price || c?.price || c?.estimated_value))
                        .slice(0, MAX_MANUAL_COMPARABLES)
                        .map((c) => ({
                        estimation_id: resolvedEstimationId,
                        comparable_id: c.id || c.source_id || c.comparable_id,
                        kind: 'direct_sale',
                        selected_price: toNumber(c.hammer_price || c.price || c.estimated_value) || 0,
                        weight: toNumber(c.effectiveWeight || c.weight) || 1,
                        note: c.comment || c.decision || c.summary || null,
                        created_by: currentUserId,
                        artist_snapshot: c.artist || c.artist_name || artwork?.artist || null,
                        title_snapshot: c.title || null,
                        source_snapshot: c.source || c.auction_house || null,
                    }));
                    if (selectedPayload.length) {
                        runWithAbortRetry(() => supabase
                            .from('estimation_selected_comparables')
                            .upsert(selectedPayload, {
                            onConflict: i18next.t("estimationSemblables.ui.estimationIdComparableId"),
                            ignoreDuplicates: false,
                        })
                            .select('*'), { retries: 1, label: i18next.t("estimationSemblables.ui.sauvegardeComparablesRetenusAutocup") })
                            .then(({ data, error }) => {
                            if (error) {
                                console.warn(i18next.t("estimationSemblables.ui.sauvegardeComparablesRetenusb4562d"), error.message);
                                return;
                            }
                            setSelectedDecisions((prev) => {
                                const rows = data || selectedPayload;
                                const byComparable = new Map();
                                [...rows, ...prev].forEach((row) => {
                                    const key = row.comparable_id || row.id || `${row.artist_snapshot || ''}|${row.title_snapshot || ''}|${row.selected_price || ''}`;
                                    const existing = byComparable.get(key);
                                    if (!existing || comparableCompletenessScore(row) > comparableCompletenessScore(existing)) {
                                        byComparable.set(key, row);
                                    }
                                });
                                return Array.from(byComparable.values());
                            });
                        })
                            .catch((error) => {
                            if (isAbortLikeError(error)) {
                                console.warn(i18next.t("estimationSemblables.ui.sauvegardeComparablesRetenusNonBloquanteRequeteAnnulee"), error?.message || error);
                                return;
                            }
                            console.warn(i18next.t("estimationSemblables.ui.sauvegardeComparablesRetenusNonBloquante"), error?.message || error);
                        });
                    }
                }
            }}/>

    {artistQuote?.method_group === 'analogy_cup' && (<ArtistMarketRatioTool artwork={artwork} currentUserId={currentUserId} disabled={!canUseAutoCUP} artistQuote={artistQuote} estimationId={resolvedEstimationId} onApplyExtrapolatedComparable={(syntheticComparable) => {
                    setExternalComparables((prev) => {
                        const next = [...prev];
                        const emptyIndex = next.findIndex((c) => !c.title && !c.artist && !c.price);
                        const mapped = {
                            title: syntheticComparable.title || '',
                            artist: syntheticComparable.artist || artwork?.artist || '',
                            source: syntheticComparable.source || 'artist_market_ratio',
                            price: syntheticComparable.price || syntheticComparable.hammer_price || '',
                            date: syntheticComparable.date || syntheticComparable.auction_date || '',
                            url: syntheticComparable.url || '',
                        };
                        if (emptyIndex >= 0)
                            next[emptyIndex] = mapped;
                        else
                            next.push(mapped);
                        return mergeComparableRows(next, MAX_MANUAL_COMPARABLES);
                    });
                    setSelectedDecisions((prev) => [
                        {
                            id: `local-${Date.now()}`,
                            kind: 'extrapolation',
                            selected_price: syntheticComparable.price || syntheticComparable.hammer_price,
                            ratio_id: syntheticComparable.ratio_id || null,
                            note: syntheticComparable.comment || '',
                            created_at: new Date().toISOString(),
                            artist_snapshot: syntheticComparable.artist || artwork?.artist || null,
                            title_snapshot: syntheticComparable.title || 'Extrapolation',
                            source_snapshot: syntheticComparable.source || 'artist_market_ratio',
                            weight: 1,
                        },
                        ...prev,
                    ]);
                }}/>)}
        </>)}

    {/* Outils "mémoire expert" : n'ont de sens que pour un expert qui travaille
        dessus, ou si un expert a déjà réellement enregistré des choix pour ce
        collectionneur. Un collectionneur sans expert ne doit pas voir un bloc
        vide "Aucun choix expert enregistré" — ça ne lui est pas destiné. */}
    {(userRole === 'expert' || userRole === 'admin' || userRole === 'team' || selectedDecisions.length > 0) && (<>
      <ExpertSelectedComparablesMemory selectedDecisions={selectedDecisions} onRemoveDecision={removeSelectedDecision}/>

      <ExpertMethodologyMemory artwork={artwork} estimationId={resolvedEstimationId} currentUserId={currentUserId} selectedDecisions={selectedDecisions} manualComparables={validComparables} autoCupComparables={autoCupRawComparables} quoteSuggestion={quoteSuggestion} artistQuote={artistQuote} confidenceScore={quoteSuggestion?.confidence_score || quoteSuggestion?.confidence || null}/>
    </>)}

    <FinalMethodologyPanel title={artistQuote?.method_group === 'analogy_cup'
                ? i18next.t("estimationSemblables.dynamic.finalGroupB", { suffix: (() => {
                    const pct = cupFinalAdjusted?.details?.prudenceDiscountPct ?? 0;
                    if (pct === 0)
                        return i18next.t("estimationSemblables.ui.b3QuasiCupDirecte");
                    if (pct <= 8)
                        return i18next.t("estimationSemblables.ui.b1HybrideRenforce");
                    return i18next.t("estimationSemblables.ui.b2AnalogiePrudente");
                })() })
                : i18next.t("estimationSemblables.ui.methodologieFinaleGroupeA")} adjustments={cupFinalAdjusted} visible={cupFinalAdjusted?.low !== null || cupFinalAdjusted?.high !== null}/>


          {/* Formulaires comparables — masqués en Groupe C et en mode personal */}
          {!isGroupCMethodGroup(artistQuote?.method_group) && valuationMode === 'cup' && (<>
              {/* Suggestions artistes analogues pour Groupe B */}
              {artistQuote?.method_group === 'analogy_cup' && Array.isArray(artistQuote?.analog_artists) && artistQuote.analog_artists.length > 0 && (<div className="border border-purple-200 bg-purple-50 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-purple-800 mb-2"><Trans i18nKey="estimationSemblables.ui.artistesAnaloguesRetenusSaisissezLeursResultatsAuxEncheresCi"/> </p>
                  <div className="flex flex-wrap gap-2">
                    {artistQuote.analog_artists.map((name) => (<button key={name} onClick={() => {
                        // Pré-remplir le premier comparable vide, ou en ajouter un nouveau
                        const emptyIdx = externalComparables.findIndex((c) => !c.artist);
                        if (emptyIdx >= 0) {
                            updateComparable(emptyIdx, 'artist', name);
                        }
                        else if (externalComparables.length < MAX_MANUAL_COMPARABLES) {
                            setExternalComparables((prev) => [
                                ...prev,
                                { ...emptyComparableRow(), artist: name }
                            ]);
                        }
                    }} className="text-xs px-3 py-1 rounded-full border border-purple-300 bg-white text-purple-700 hover:bg-purple-100 transition">
                        + {name}
                      </button>))}
                  </div>
                  <p className="text-[11px] text-purple-600 mt-2"><Trans i18nKey="estimationSemblables.ui.cliquezSurUnNomPourPreRemplirLeChamp"/> </p>
                </div>)}
              {externalComparables.map((comp, index) => (<div key={index} className="border border-gray-200 rounded-lg p-6 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold"><Trans i18nKey="estimationSemblables.ui.comparable"/>{index + 1}</h3>
                      {isArtistDeceasedInCurrentYear(comp) && (<span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><Trans i18nKey="estimationSemblables.ui.decesRecent"/> </span>)}
                    </div>

                    {externalComparables.length > 1 && (<button onClick={() => removeComparable(index)} className="text-sm text-red-600 hover:text-red-800">
                        Supprimer
                      </button>)}
                  </div>

                  {comp.marketAlert && (<div className="mb-4 border border-amber-200 bg-amber-50 text-amber-800 rounded px-3 py-2 text-xs">
                      {comp.marketAlert}
                    </div>)}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1"><Trans i18nKey="estimationSemblables.ui.titreDeLUvre"/> <span className="text-red-500">*</span>
                      </label>
                      <input type="text" value={comp.title} onChange={(e) => updateComparable(index, 'title', e.target.value)} placeholder={i18next.t("estimationSemblables.ui.exSansTitreSerieAbstraite")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Artiste <span className="text-red-500">*</span>
                      </label>
                      <input type="text" value={comp.artist} onChange={(e) => updateComparable(index, 'artist', e.target.value)} placeholder={i18next.t("estimationSemblables.ui.nomDeLArtiste")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1"><Trans i18nKey="estimationSemblables.ui.prixDeVente"/> <span className="text-red-500">*</span>
                      </label>
                      <input type="number" value={comp.price} onChange={(e) => updateComparable(index, 'price', e.target.value)} placeholder={i18next.t("estimationSemblables.ui.ex15000")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1"><Trans i18nKey="estimationSemblables.ui.dateDeVente"/> </label>
                      <input type="date" value={comp.date} onChange={(e) => updateComparable(index, 'date', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Source <span className="text-red-500">*</span>
                      </label>
                      <input type="text" value={comp.source} onChange={(e) => updateComparable(index, 'source', e.target.value)} placeholder={i18next.t("estimationSemblables.ui.exArtpriceChristieS")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1"><Trans i18nKey="estimationSemblables.ui.lienOptionnel"/> </label>
                      <input type="url" value={comp.url} onChange={(e) => updateComparable(index, 'url', e.target.value)} placeholder="https://..." className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                    </div>
                  </div>
                </div>))}

              <button onClick={addComparable} disabled={externalComparables.length >= MAX_MANUAL_COMPARABLES} className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-black transition text-gray-600 hover:text-black disabled:opacity-50"><Trans i18nKey="estimationSemblables.ui.ajouterUneUvreComparable"/>{externalComparables.length}/{MAX_MANUAL_COMPARABLES})
              </button>
            </>)}
        </div>

        {/* Bandeau abonnement */}
        {subscriptionLevel === 'standard' && (<div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-2"><Trans i18nKey="estimationSemblables.ui.validationParLEquipeArtkiv"/> </h3>
            <p className="text-sm text-amber-800"><Trans i18nKey="estimationSemblables.ui.votreAbonnement"/> <strong>Standard</strong><Trans i18nKey="estimationSemblables.ui.inclutUneValidationHumaineUneFoisVosComparablesAjoutes"/> <strong>48-72h</strong>.
            </p>
          </div>)}

        {subscriptionLevel === 'premium' && (<div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8">
            <h3 className="font-semibold text-green-900 mb-2"><Trans i18nKey="estimationSemblables.ui.validationPrioritairePremium"/> </h3>
            <p className="text-sm text-green-800"><Trans i18nKey="estimationSemblables.ui.votreAbonnement"/> <strong>Premium</strong><Trans i18nKey="estimationSemblables.ui.vousDonneAccesAUneValidation"/> <strong><Trans i18nKey="estimationSemblables.ui.sous24h"/></strong><Trans i18nKey="estimationSemblables.ui.parNotreEquipeDExperts"/> </p>
          </div>)}

        {/* Bloc expert Vision — masqué en Groupe C */}
        {(userRole === 'expert' || userRole === 'admin' || userRole === 'team') && !isGroupCMethodGroup(artistQuote?.method_group) && (<div className="bg-white border border-gray-200 rounded-lg p-8 mb-8">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-2xl font-serif mb-2"><Trans i18nKey="estimationSemblables.ui.affinageVision"/></h2>
                <p className="text-gray-600 text-sm"><Trans i18nKey="estimationSemblables.ui.outilExpertPourAffinerLesComparablesEtEnrichirL"/> </p>
              </div>

              {!canUseExpertVision && (<span className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded"><Trans i18nKey="estimationSemblables.ui.accesVisionCompletRequis"/> </span>)}
            </div>

            {!canUseExpertVision ? (<div className="bg-amber-50 border border-amber-200 rounded p-4">
                <p className="text-sm text-amber-900"><Trans i18nKey="estimationSemblables.ui.votreCompteExpertEstReconnuMaisLAnalyseVision"/> <strong><Trans i18nKey="estimationSemblables.ui.artAdvisorPremium"/></strong>.
                </p>
              </div>) : validComparables.length < 3 ? (<div className="bg-gray-50 border border-gray-200 rounded p-4">
                <p className="text-sm text-gray-800"><Trans i18nKey="estimationSemblables.ui.lAnalyseVisionSeraDisponibleDesQueVousAurez"/>{' '}
                  <strong><Trans i18nKey="estimationSemblables.ui.3ComparablesComplets"/></strong>.
                </p>
                <p className="text-xs text-gray-500 mt-2"><Trans i18nKey="estimationSemblables.ui.importezDesFichesArtpriceOuAjoutezManuellementDesComparables"/> </p>
              </div>) : (<>
                <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
                  <p className="text-sm text-blue-900"><Trans i18nKey="estimationSemblables.ui.lancezUneAnalyseVisionPourAffinerLaCoherenceDes"/> </p>
                </div>

                <button type="button" onClick={runVisionComparableAnalysis} disabled={visionLoading} className="px-6 py-3 bg-black text-white rounded hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium">
                  {visionLoading ? i18next.t("estimationSemblables.ui.analyseVisionEnCours") : i18next.t("estimationSemblables.ui.analyserAvecVision")}
                </button>

                {visionResult && (<div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold mb-3"><Trans i18nKey="estimationSemblables.ui.resultatVision"/></h3>

                    {visionResult.summary && (<p className="text-sm text-gray-800 mb-3">
                        {visionResult.summary}
                      </p>)}

                    {Array.isArray(visionResult.recommendations) &&
                        visionResult.recommendations.length > 0 && (<div className="mb-3">
                          <h4 className="text-sm font-medium mb-2">Recommandations</h4>
                          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                            {visionResult.recommendations.map((rec, idx) => (<li key={idx}>{rec}</li>))}
                          </ul>
                        </div>)}

                    {visionResult.estimated_range && (<div className="text-sm text-gray-700">
                        <strong><Trans i18nKey="estimationSemblables.ui.fourchetteSuggeree"/></strong>{' '}
                        {visionResult.estimated_range.low} € – {visionResult.estimated_range.high} €
                      </div>)}

                    {!visionResult.summary &&
                        !visionResult.estimated_range &&
                        !Array.isArray(visionResult.recommendations) && (<pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
                          {JSON.stringify(visionResult, null, 2)}
                        </pre>)}
                  </div>)}
              </>)}
          </div>)}

        <FinalMethodologyPanel title={isGroupCMethodGroup(artistQuote?.method_group)
            ? i18next.t("estimationSemblables.ui.recapitulatifFinalGroupeC") : valuationMode === 'personal'
            ? i18next.t("estimationSemblables.ui.recapitulatifFinalCotationPersonnelle") : artistQuote?.method_group === 'analogy_cup'
            ? i18next.t("estimationSemblables.dynamic.finalSummaryGroupB", { suffix: (() => {
                const pct = cupFinalAdjusted?.details?.prudenceDiscountPct ?? 0;
                if (pct === 0)
                    return i18next.t("estimationSemblables.ui.b3QuasiCupDirecte");
                if (pct <= 8)
                    return i18next.t("estimationSemblables.ui.b1HybrideRenforce");
                return i18next.t("estimationSemblables.ui.b2AnalogiePrudente");
            })() })
            : i18next.t("estimationSemblables.ui.recapitulatifFinalGroupeA")} adjustments={isGroupCMethodGroup(artistQuote?.method_group)
            ? quoteSuggestion?.finalAdjustments
            : valuationMode === 'personal'
                ? personalQuoteAdjusted
                : cupFinalAdjusted} visible={Boolean(artistQuote)}/>

        {/* ── Alerte diagnostic : état de conservation non reconnu par la grille ── */}
        {(() => {
            const disc = getConditionDiscount(artwork?.condition);
            if (!disc.isUnrecognized)
                return null;
            return (<div className="w-full rounded-lg border border-orange-400 bg-orange-50 p-3 mb-2 text-xs text-orange-900">
              <strong><Trans i18nKey="estimationSemblables.ui.etatDeConservationNonReconnu"/></strong><Trans i18nKey="estimationSemblables.ui.laValeurStockeeEnBaseEst"/>{' '}
              <code className="bg-orange-100 px-1 rounded"><Trans i18nKey="estimationSemblables.ui.laquoNbsp"/>{artwork.condition}<Trans i18nKey="estimationSemblables.ui.nbspRaquo"/></code>.{' '}<Trans i18nKey="estimationSemblables.ui.aucuneDecoteNEstAppliqueeValeursAttendues"/>{' '}
              <code>fair</code> (−20%), <code>very_poor</code> (−30%), <code>heavily_damaged</code> (−50%),{' '}
              <code>partially_restorable</code> (−65%), <code>irreparable</code><Trans i18nKey="estimationSemblables.ui.writeOff"/>{' '}<Trans i18nKey="estimationSemblables.ui.corrigerLaFicheUvrePourAppliquerLaDecoteCorrectement"/> </div>);
        })()}

        {/* Note libre de l'expert — visible en mode CUP et AutoCUP avant validation */}
        {['expert', 'admin', 'team'].includes(userRole) && !writeOffMode && (<div className="w-full rounded-lg border border-[#C9A961]/40 bg-[#F9F7F4] p-4 mb-2">
            <label className="block text-xs font-semibold text-[#1A1A1A] mb-1 tracking-wide uppercase"><Trans i18nKey="estimationSemblables.ui.noteDeLExpertJustificationDeLEstimation"/> </label>
            <p className="text-xs text-gray-500 mb-2"><Trans i18nKey="estimationSemblables.ui.cetteNoteApparaitDansLeRapportDExpertiseElle"/> </p>
            <textarea value={expertFreeNote} onChange={(e) => setExpertFreeNote(e.target.value)} placeholder={i18next.t("estimationSemblables.ui.exUvreEstimeeParAnalogieAvecLesSerigraphiesDu")} rows={4} className="w-full text-sm border border-[#C9A961]/50 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#C9A961] resize-y font-serif"/>
          </div>)}

        {/* Actions */}
        <div id="estimation-submit-actions" className="flex gap-4 flex-wrap" style={{ scrollMarginTop: 80 }}>
          {/* Panneau write-off inline */}
          {writeOffMode && (<div id="write-off-panel" className="w-full rounded-lg border-2 border-red-300 bg-red-50 p-5 mb-2">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-red-900 text-sm"><Trans i18nKey="estimationSemblables.ui.classementIrreparableValeurNulle"/></p>
                  <p className="text-xs text-red-700 mt-1 leading-relaxed"><Trans i18nKey="estimationSemblables.ui.cetteActionEnregistre"/> <strong>0 €</strong><Trans i18nKey="estimationSemblables.ui.commeValeurRetenueEtAppliqueLaMethode"/> <em>write-off</em><Trans i18nKey="estimationSemblables.ui.surLaFicheOeuvreDocumentezObligatoirementLaDecisionCi"/> </p>
                </div>
              </div>
              <label className="block text-xs font-medium text-red-900 mb-1"><Trans i18nKey="estimationSemblables.ui.noteDeJustification"/> <span className="text-red-500">*</span>
              </label>
              <textarea value={writeOffNote} onChange={(e) => setWriteOffNote(e.target.value)} placeholder={i18next.t("estimationSemblables.ui.exEtatIrreparableConstateLorsDeLaVisiteDu")} rows={3} className="w-full text-sm border border-red-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-red-400 resize-y"/>
              <div className="flex gap-3 mt-4">
                <button onClick={handleWriteOffSubmit} disabled={!writeOffNote.trim() || writeOffSaving} className="px-5 py-2.5 bg-red-700 text-white text-sm font-semibold rounded hover:bg-red-800 disabled:bg-red-300 disabled:cursor-not-allowed transition">
                  {writeOffSaving ? i18next.t("estimationSemblables.ui.enregistrement") : i18next.t("estimationSemblables.ui.confirmerValeur0")}
                </button>
                <button onClick={() => { setWriteOffMode(false); setWriteOffNote(''); }} className="px-5 py-2.5 border-2 border-red-300 text-red-800 text-sm rounded hover:border-red-500 transition">
                  Annuler
                </button>
              </div>
            </div>)}

          <button onClick={handleSubmit} disabled={!canContinueToValidation && !(quoteSuggestion?.isWriteOff || (valuationMode === 'personal' && personalConditionDiscount?.pct === 100))} className={`flex-1 min-w-[220px] px-6 py-3 rounded font-medium transition ${quoteSuggestion?.isWriteOff || (valuationMode === 'personal' && personalConditionDiscount?.pct === 100)
            ? 'bg-red-700 text-white hover:bg-red-800'
            : 'bg-black text-white hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed'}`}>
            {quoteSuggestion?.isWriteOff || (valuationMode === 'personal' && personalConditionDiscount?.pct === 100)
            ? i18next.t("estimationSemblables.ui.documenterLeWriteOffValeur0") : ['expert', 'admin', 'team'].includes(userRole)
            ? i18next.t("estimationSemblables.ui.enregistrerLEstimation") : i18next.t("estimationSemblables.ui.continuerVersLaValidation")}
          </button>

          {/* Bouton appliquer au lot — visible seulement si artwork dans un lot avec au moins 1 autre membre */}
          {artwork?.lot_id && lotMembers.length > 0 && canContinueToValidation && (<button onClick={() => setLotApplyOpen(true)} disabled={!canContinueToValidation || lotMembersLoading} className="flex-1 min-w-[260px] px-6 py-3 bg-white text-[#1A1A1A] border-2 border-[#C9A961] rounded hover:bg-[#F9F7F4] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition flex items-center justify-center gap-2" title={i18next.t("estimationSemblables.ui.appliquerCetteEstimationAToutesLesUvresDuLot")}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg><Trans i18nKey="estimationSemblables.ui.enregistrerAppliquerAuLot"/>{lotMembers.length + 1})
            </button>)}

          <button onClick={() => navigate('/estimation')} className="px-6 py-3 border-2 border-gray-300 rounded hover:border-black">
            Annuler
          </button>
        </div>

        {/* Bandeau tarification */}
        <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4">
          <p className="text-sm text-blue-900 font-light leading-relaxed">
            <strong><Trans i18nKey="estimationSemblables.ui.serviceDeValidation"/></strong>
            <br />
            • <strong>Standard</strong><Trans i18nKey="estimationSemblables.ui.10ValidationsGratuitesMoisPuis150ParValidation"/> <br />
            • <strong>Premium</strong><Trans i18nKey="estimationSemblables.ui.50ValidationsGratuitesMoisPuis150ParValidation"/> <br /><Trans i18nKey="estimationSemblables.ui.nosExpertsVerifierontVosComparablesSous4872hOuvres"/> </p>
        </div>

        {/* Info supplémentaire */}
        <div className="mt-12 bg-white border border-gray-200 rounded-lg p-8">
          <h3 className="text-lg font-serif mb-4"><Trans i18nKey="estimationSemblables.ui.questionsFrequentes"/></h3>

          <details className="mb-3">
            <summary className="cursor-pointer font-medium text-sm"><Trans i18nKey="estimationSemblables.ui.combienDeComparablesDoisJeAjouter"/> </summary>
            <p className="text-sm text-gray-700 mt-2 pl-4"><Trans i18nKey="estimationSemblables.ui.groupeAMinimum3ComparablesGroupeBIdealement5"/> </p>
          </details>

          <details className="mb-3">
            <summary className="cursor-pointer font-medium text-sm"><Trans i18nKey="estimationSemblables.ui.quelsCriteresPourChoisirUneUvreComparable"/> </summary>
            <p className="text-sm text-gray-700 mt-2 pl-4"><Trans i18nKey="estimationSemblables.ui.privilegiezMemeArtisteMemePeriodeStyleDimensionsSimilairesTechni"/> </p>
          </details>

          <details>
            <summary className="cursor-pointer font-medium text-sm"><Trans i18nKey="estimationSemblables.ui.combienDeTempsPourRecevoirLEstimationValidee"/> </summary>
            <p className="text-sm text-gray-700 mt-2 pl-4"><Trans i18nKey="estimationSemblables.ui.standard4872hPremiumSous24hJoursOuvres"/> </p>
          </details>
        </div>

        {/* ── Modale : récapitulatif application au lot ───────────────────── */}
        {lotApplyOpen && artwork?.lot_id && lotPreview && (<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget && !lotApplying)
            setLotApplyOpen(false); }}>
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ background: '#F9F7F4' }}>
              {/* En-tête */}
              <div className="px-6 py-5 border-b border-gray-200 bg-white">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1"><Trans i18nKey="estimationSemblables.ui.applicationAuLot"/></p>
                <h3 className="text-xl font-serif text-[#1A1A1A]">
                  {artwork?.artist || i18next.t("estimationSemblables.dynamic.lot")}<Trans i18nKey="estimationSemblables.ui.appliquerLEstimationA"/> {lotMembers.length + 1}<Trans i18nKey="estimationSemblables.ui.uvres"/> </h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed"><Trans i18nKey="estimationSemblables.ui.lEstimationDeReferenceEstDupliqueeSurToutesLes"/> </p>
              </div>

              {/* Corps */}
              <div className="px-6 py-5 overflow-y-auto flex-1 bg-white">
                {!lotApplyResult ? (<>
                    {/* Estimation de référence */}
                    <div className="mb-5 p-4 border border-gray-200 rounded">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2"><Trans i18nKey="estimationSemblables.ui.estimationDeReferenceAvantDecoteEtat"/></p>
                      <div className="flex items-baseline gap-6">
                        <div>
                          <p className="text-xs text-gray-500">Basse</p>
                          <p className="text-lg font-serif">{lotPreview.baseLow !== null ? formatEuro(lotPreview.baseLow) : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Retenue</p>
                          <p className="text-lg font-serif font-medium">{lotPreview.baseRetained !== null ? formatEuro(lotPreview.baseRetained) : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Haute</p>
                          <p className="text-lg font-serif">{lotPreview.baseHigh !== null ? formatEuro(lotPreview.baseHigh) : '—'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Répartition par état */}
                    <div className="mb-5">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2"><Trans i18nKey="estimationSemblables.ui.repartitionParEtatDeConservation"/></p>
                      <div className="border border-gray-200 rounded overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-4 py-2 font-normal text-xs text-gray-600 uppercase tracking-wider"><Trans i18nKey="estimationSemblables.ui.etat"/></th>
                              <th className="text-right px-4 py-2 font-normal text-xs text-gray-600 uppercase tracking-wider"><Trans i18nKey="estimationSemblables.ui.nbUvres"/></th>
                              <th className="text-right px-4 py-2 font-normal text-xs text-gray-600 uppercase tracking-wider"><Trans i18nKey="estimationSemblables.ui.valeurRetenueUnite"/></th>
                            </tr>
                          </thead>
                          <tbody>
                            {lotPreview.groups.map((g, i) => {
                    const dotColor = g.pct === 0 ? i18next.t("estimationSemblables.ui.1d9e75") : g.pct <= 20 ? i18next.t("estimationSemblables.ui.ef9f27") : g.pct <= 30 ? i18next.t("estimationSemblables.ui.d85a30") : g.pct >= 100 ? i18next.t("estimationSemblables.ui.1a1a1a") : i18next.t("estimationSemblables.ui.a32d2d");
                    return (<tr key={i} className="border-t border-gray-100">
                                  <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, display: 'inline-block' }}/>
                                      <span>{g.label}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5 text-right tabular-nums">{g.count}</td>
                                  <td className="px-4 py-2.5 text-right tabular-nums font-serif">{g.retained !== null ? formatEuro(g.retained) : '—'}</td>
                                </tr>);
                })}
                          </tbody>
                          <tfoot className="bg-gray-50 border-t border-gray-200">
                            <tr>
                              <td className="px-4 py-2.5 text-xs text-gray-600 uppercase tracking-wider"><Trans i18nKey="estimationSemblables.ui.totalLot"/></td>
                              <td className="px-4 py-2.5 text-right tabular-nums font-medium">{lotPreview.totalCount}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums font-serif font-medium">{formatEuro(lotPreview.totalRetained)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 italic"><Trans i18nKey="estimationSemblables.ui.fourchetteTotaleLot"/> {formatEuro(lotPreview.totalLow)} – {formatEuro(lotPreview.totalHigh)}
                      </p>
                    </div>

                    {/* Détail par œuvre — repliable */}
                    <details className="mb-2">
                      <summary className="text-xs text-gray-600 uppercase tracking-wider cursor-pointer hover:text-[#C9A961]"><Trans i18nKey="estimationSemblables.ui.voirLeDetailUvreParUvre"/>{lotPreview.rows.length})
                      </summary>
                      <div className="mt-3 border border-gray-200 rounded overflow-hidden max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-2 font-normal text-gray-600"><Trans i18nKey="estimationSemblables.ui.nInv"/></th>
                              <th className="text-left px-3 py-2 font-normal text-gray-600"><Trans i18nKey="estimationSemblables.ui.etat"/></th>
                              <th className="text-right px-3 py-2 font-normal text-gray-600"><Trans i18nKey="estimationSemblables.ui.valeurRetenue"/></th>
                            </tr>
                          </thead>
                          <tbody>
                            {lotPreview.rows.map((r) => (<tr key={r.id} className={`border-t border-gray-100 ${r.isCurrent ? 'bg-[#F9F7F4]' : ''}`}>
                                <td className="px-3 py-1.5">
                                  {r.inventory_number || r.id.slice(0, 8)}
                                  {r.isCurrent && <span className="ml-2 text-[10px] text-[#C9A961] uppercase tracking-wider"><Trans i18nKey="estimationSemblables.ui.uvreSource"/></span>}
                                </td>
                                <td className="px-3 py-1.5 text-gray-600">{r.conditionLabel}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums">{r.retained !== null ? formatEuro(r.retained) : '—'}</td>
                              </tr>))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </>) : (
            /* Résultat après application */
            <div className="text-center py-6">
                    {lotApplyResult.errors.length === 0 ? (<>
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                        <p className="text-lg font-serif mb-2"><Trans i18nKey="estimationSemblables.ui.estimationAppliqueeA"/> {lotApplyResult.success}<Trans i18nKey="estimationSemblables.ui.uvres"/></p>
                        <p className="text-sm text-gray-600"><Trans i18nKey="estimationSemblables.ui.chaqueUvreAEteMiseAJourAvecSa"/></p>
                      </>) : (<>
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#BA7517" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                          </svg>
                        </div>
                        <p className="text-lg font-serif mb-2">{lotApplyResult.success}<Trans i18nKey="estimationSemblables.ui.appliquees"/> {lotApplyResult.errors.length}<Trans i18nKey="estimationSemblables.ui.enErreur"/></p>
                        <ul className="text-xs text-left text-red-700 mt-3 max-h-32 overflow-y-auto bg-red-50 p-3 rounded">
                          {lotApplyResult.errors.slice(0, 5).map((e, i) => (<li key={i}>• {e.inv || e.id?.slice(0, 8) || 'inconnu'} — {e.message}</li>))}
                        </ul>
                      </>)}
                  </div>)}
              </div>

              {/* Pied — actions */}
              <div className="px-6 py-4 border-t border-gray-200 bg-white flex gap-3 justify-end">
                {!lotApplyResult ? (<>
                    <button onClick={() => setLotApplyOpen(false)} disabled={lotApplying} className="px-5 py-2.5 border border-gray-300 rounded text-sm hover:border-[#1A1A1A] disabled:opacity-50">
                      Annuler
                    </button>
                    <button onClick={async () => {
                    // 1. Sauvegarder l'œuvre courante d'abord
                    const ok = await saveValuationToArtwork();
                    if (!ok) {
                        setLotApplyResult({ success: 0, errors: [{ message: i18next.t("estimationSemblables.ui.echecSauvegardeUvreSourceLotNonModifie") }] });
                        return;
                    }
                    await saveManualComparablesToDB();
                    // 2. Appliquer au reste du lot
                    await applyEstimationToLot();
                }} disabled={lotApplying || !canContinueToValidation} className="px-5 py-2.5 bg-[#1A1A1A] text-white rounded text-sm font-medium hover:bg-black disabled:opacity-50 flex items-center gap-2">
                      {lotApplying ? (<>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                            <line x1="12" y1="2" x2="12" y2="6"></line>
                            <line x1="12" y1="18" x2="12" y2="22"></line>
                            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                          </svg><Trans i18nKey="estimationSemblables.ui.applicationEnCours"/> </>) : (i18next.t("estimationSemblables.dynamic.confirmApplyLot", { count: lotMembers.length + 1 }))}
                    </button>
                  </>) : (<button onClick={() => {
                    setLotApplyOpen(false);
                    setLotApplyResult(null);
                    // Retour fiche œuvre après application réussie
                    if (lotApplyResult.errors.length === 0) {
                        const artworkRoute = ['expert', 'admin', 'team'].includes(userRole)
                            ? `/expert/artwork/${artwork.id}`
                            : `/artwork/${artwork.id}`;
                        navigate(artworkRoute, { state: { flash: i18next.t("estimationSemblables.dynamic.appliedLot", { count: lotApplyResult.success + 1 }) } });
                    }
                }} className="px-5 py-2.5 bg-[#1A1A1A] text-white rounded text-sm font-medium hover:bg-black">
                    Fermer
                  </button>)}
              </div>
            </div>
          </div>)}
      </main>
    </div>);
}
// --- ARTKIV UI market segmentation patch ---
export function buildComparableMarketLabel(row, targetArtwork) {
    const segment = getComparableSegment(row, targetArtwork);
    if (segment === 'analog_trend') {
        return i18next.t("estimationSemblables.marketLabel.analogTrend");
    }
    return SEGMENT_LABELS[segment]?.() || i18next.t("estimationSemblables.marketLabel.comparable");
}
